import { useState, useEffect, useCallback, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { gmailService } from '../services/api/gmailService';
import { calendarService, CalendarInfo, CalendarEvent } from '../services/api/calendarService';
import { empresasService, DadosEmpresa } from '../services/api/empresasService';
import { disponibilidadesService, Disponibilidade } from '../services/api/disponibilidadesService';
import { templatesNotificacaoService } from '../services/api/templatesNotificacaoService';
import { TemplateNotificacao } from '../types/templateNotificacao';
import { notificationsService, NotificacaoPreferencias } from '../services/notificationsService';
import ThemeConfiguration from '../components/ThemeConfiguration';
import TemplatePreviewModal from '../components/TemplatePreviewModal';

// Tipos para as configurações
interface HorarioTrabalho {
  id: string;
  diaSemana: number;
  ativo: boolean;
  horaInicio: string;
  horaFim: string;
  intervalo_minutos?: number;
  observacoes?: string;
  usuario_id?: number;
  intervalos: {
    id: string;
    horaInicio: string;
    horaFim: string;
    descricao: string;
    temporario?: boolean; // Flag para identificar intervalos não salvos
  }[];
}

interface IntegracaoGoogle {
  emailHabilitado: boolean;
  agendaHabilitada: boolean;
  clientId: string;
  clientSecret: string;
  emailRemetente: string;
  nomeRemetente: string;
  assinaturaEmail: string;
  agendaId: string;
  fusoHorario: string;
}

export default function Configuracoes() {
  const navigate = useNavigate();
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [abaSelecionada, setAbaSelecionada] = useState<'empresa' | 'horarios' | 'tema' | 'google' | 'notificacoes' | 'alertas'>('empresa');
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);

  // Estados para dados da empresa
  const [dadosEmpresa, setDadosEmpresa] = useState<DadosEmpresa>({
    id: 0,
    nome: '',
    nome_fantasia: '',
    cnpj: '',
    email: '',
    telefone: '',
    endereco: '',
    cep: '',
    cidade: '',
    estado: '',
    // logo: '', // Campo removido - não existe mais na tabela empresas
    ativo: true
  });

  // Estados para horários de trabalho
  const [horariosTrabalho, setHorariosTrabalho] = useState<HorarioTrabalho[]>([
    { id: '0', diaSemana: 0, ativo: false, horaInicio: '08:00', horaFim: '18:00', intervalos: [] },
    { id: '1', diaSemana: 1, ativo: true, horaInicio: '08:00', horaFim: '18:00', intervalos: [{ id: '1-1', horaInicio: '12:00', horaFim: '13:00', descricao: 'Almoço' }] },
    { id: '2', diaSemana: 2, ativo: true, horaInicio: '08:00', horaFim: '18:00', intervalos: [{ id: '2-1', horaInicio: '12:00', horaFim: '13:00', descricao: 'Almoço' }] },
    { id: '3', diaSemana: 3, ativo: true, horaInicio: '08:00', horaFim: '18:00', intervalos: [{ id: '3-1', horaInicio: '12:00', horaFim: '13:00', descricao: 'Almoço' }] },
    { id: '4', diaSemana: 4, ativo: true, horaInicio: '08:00', horaFim: '18:00', intervalos: [{ id: '4-1', horaInicio: '12:00', horaFim: '13:00', descricao: 'Almoço' }] },
    { id: '5', diaSemana: 5, ativo: true, horaInicio: '08:00', horaFim: '18:00', intervalos: [{ id: '5-1', horaInicio: '12:00', horaFim: '13:00', descricao: 'Almoço' }] },
    { id: '6', diaSemana: 6, ativo: false, horaInicio: '08:00', horaFim: '12:00', intervalos: [] }
  ]);

  // Estados para integração Google
  const [integracaoGoogle, setIntegracaoGoogle] = useState<IntegracaoGoogle>({
    emailHabilitado: false,
    agendaHabilitada: false,
    clientId: '',
    clientSecret: '',
    emailRemetente: '',
    nomeRemetente: '',
    assinaturaEmail: '',
    agendaId: '',
    fusoHorario: 'America/Sao_Paulo'
  });

  // Estados para conexão OAuth Google
  const [conectandoGoogle, setConectandoGoogle] = useState(false);
  const [contaGoogleConectada, setContaGoogleConectada] = useState(false);
  const [dadosContaGoogle, setDadosContaGoogle] = useState<{
    email: string;
    nome: string;
    conectadoEm: string;
  } | null>(null);

  // Estados para teste de email
  const [emailTeste, setEmailTeste] = useState({
    destinatario: '',
    assunto: 'Teste de Envio - Sistema de Psicologia',
    mensagem: 'Este é um email de teste enviado através do sistema de gestão de consultório de psicologia.\n\nSe você recebeu esta mensagem, a integração com Gmail está funcionando corretamente!'
  });
  const [enviandoTeste, setEnviandoTeste] = useState(false);

  // Estado para upload de logo
  const [uploadandoLogo, setUploadandoLogo] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  // Estados para Google Calendar
  const [calendarios, setCalendarios] = useState<CalendarInfo[]>([]);
  const [calendarioSelecionado, setCalendarioSelecionado] = useState('');
  const [carregandoCalendarios, setCarregandoCalendarios] = useState(false);
  const [eventoTeste, setEventoTeste] = useState({
    titulo: 'Teste - Sistema de Psicologia',
    descricao: 'Este é um evento de teste criado pelo sistema de gestão de consultório de psicologia.\n\nSe você vê este evento, a integração com Google Calendar está funcionando corretamente!',
    dataInicio: '',
    horaInicio: '09:00',
    dataFim: '',
    horaFim: '10:00'
  });
  const [criandoEventoTeste, setCriandoEventoTeste] = useState(false);

  // Estados para exclusão de evento
  const [eventoParaExcluir, setEventoParaExcluir] = useState({
    eventId: '',
    calendarioId: ''
  });
  const [excluindoEvento, setExcluindoEvento] = useState(false);

  // Estados para edição de evento
  const [eventoParaEditar, setEventoParaEditar] = useState({
    eventId: '',
    titulo: '',
    descricao: '',
    dataInicio: '',
    horaInicio: '',
    dataFim: '',
    horaFim: ''
  });
  const [editandoEvento, setEditandoEvento] = useState(false);

  // Estados para listar eventos
  const [eventos, setEventos] = useState<any[]>([]);
  const [carregandoEventos, setCarregandoEventos] = useState(false);

  // Estados para disponibilidades
  const [carregandoDisponibilidades, setCarregandoDisponibilidades] = useState(false);

  // Estados para templates de notificação
  const [templatesNotificacao, setTemplatesNotificacao] = useState<TemplateNotificacao>({
    codEmpresa: 0,
    usuarioId: 0,
    emailConfirmacaoHtml: '',
    emailCancelamentoHtml: '',
    calendarTituloTemplate: 'Consulta - {paciente_nome}',
    calendarDescricaoTemplate: '',
    calendarLocalizacaoTemplate: '{link_consulta_online}',
    incluirLinkConsulta: true,
    incluirObservacoesImportantes: true,
    // Personalização visual
    corPrimaria: '#4a90a4',
    corSecundaria: '#28a745',
    corTexto: '#333333',
    corFundo: '#f8f9fa',
    usarLogoEmpresa: true,
    nomeExibicao: '',
    mensagemBoasVindas: 'Olá {paciente_nome}, confirmamos sua consulta!',
    mensagemRodape: 'Qualquer dúvida, entre em contato conosco.',
    estiloTemplate: 'moderno'
  });
  const [salvandoTemplates, setSalvandoTemplates] = useState(false);
  
  // Estado para controlar o modal de preview dos templates
  const [mostrarPreviewTemplates, setMostrarPreviewTemplates] = useState(false);

  // Estados para preferências de alertas in-app
  const [preferenciasAlertas, setPreferenciasAlertas] = useState<NotificacaoPreferencias>({
    notificacoes_habilitadas: true,
    som_habilitado: true,
    alerta_antes_consulta: 15,
    alerta_pagamento_apos_consulta: 60,
    alerta_prontuario_pendente: 120,
    alerta_plano_vencimento: 7,
    tipo_lembrete_consulta: true,
    tipo_pagamento_aprovado: true,
    tipo_pagamento_pendente: true,
    tipo_prontuario_pendente: true,
    tipo_plano_vencendo: true
  });
  const [salvandoPreferenciasAlertas, setSalvandoPreferenciasAlertas] = useState(false);
  const [carregandoPreferenciasAlertas, setCarregandoPreferenciasAlertas] = useState(false);

  // Dados do usuário logado
  const userData = authService.getUsuario();
  const usuarioId = userData?.id;
  const [mostrarEventos, setMostrarEventos] = useState(false);
  const [filtroEventos, setFiltroEventos] = useState(() => {
    const agora = new Date();
    const inicioMes = new Date(agora.getFullYear(), agora.getMonth(), 1);
    const fimMes = new Date(agora.getFullYear(), agora.getMonth() + 2, 0); // 2 meses à frente
    
    return {
      dataInicio: inicioMes.toISOString().split('T')[0],
      dataFim: fimMes.toISOString().split('T')[0]
    };
  });

  // Carregar dados iniciais
  useEffect(() => {
    carregarConfiguracoes();
    carregarLogoEmpresa();
  }, []);

  // Verificar callback do OAuth2 Google
  useEffect(() => {
    const verificarCallback = async () => {
      const callback = gmailService.verificarCallback();
      
      if (callback.sucesso) {
        setSucesso('Conta Google conectada com sucesso! As configurações foram atualizadas automaticamente.');
        // Recarregar dados do Gmail para atualizar o status
        try {
          const statusGoogle = await gmailService.verificarConexao();
          if (statusGoogle.sucesso && statusGoogle.dados) {
            setContaGoogleConectada(true);
            setDadosContaGoogle({
              email: statusGoogle.dados.email,
              nome: statusGoogle.dados.nome || statusGoogle.dados.email,
              conectadoEm: statusGoogle.dados.conectadoEm
            });
            setIntegracaoGoogle(prev => ({
              ...prev,
              emailHabilitado: true,
              agendaHabilitada: true, // Habilitar Google Calendar automaticamente
              emailRemetente: statusGoogle.dados.email,
              nomeRemetente: statusGoogle.dados.nome || statusGoogle.dados.email
            }));
            
            // console.log('[Configuracoes] Status do Gmail atualizado após callback:', {
              // email: statusGoogle.dados.email,
              // conectada: true
            // });
            
            // IMPORTANTE: Recarregar tokens do Calendar também após nova conexão
            try {
              // console.log('[Configuracoes] Recarregando tokens do Calendar após OAuth callback...');
              await calendarService.recarregarTokens();
              // console.log('[Configuracoes] Tokens do Calendar recarregados');
            } catch (calendarError) {
              console.error('[Configuracoes] Erro ao recarregar tokens do Calendar após callback:', calendarError);
              // Não falhar a operação principal por erro no calendar
            }
          }
        } catch (error) {
          console.error('[Configuracoes] Erro ao verificar status após callback:', error);
        }
      } else if (callback.erro) {
        setErro(`Erro ao conectar conta Google: ${callback.erro}`);
      }
    };

    verificarCallback();
  }, []);

  // Função para carregar URL da logo da empresa
  const carregarLogoEmpresa = useCallback(async () => {
    try {
      const resultado = await empresasService.buscarLogoUrl();
      if (resultado.sucesso && resultado.dados?.url) {
        setLogoUrl(resultado.dados.url);
      } else {
        setLogoUrl(null);
      }
    } catch (error) {
      console.error('[Configuracoes] Erro ao carregar logo:', error);
      setLogoUrl(null);
    }
  }, []);

  const carregarConfiguracoes = useCallback(async () => {
    try {
      setCarregando(true);
      setErro(null);

      // VALIDAÇÃO CRÍTICA: Verificar autenticação com rigor militar
      const token = sessionStorage.getItem('token');
      if (!token) {
        console.error('[SECURITY-CHECK] Token não encontrado - redirecionando para login');
        navigate('/login');
        return;
      }

      if (!authService.isAuthenticated()) {
        console.error('[SECURITY-CHECK] Usuário não autenticado - redirecionando para login');
        navigate('/login');
        return;
      }

      const usuario = authService.getUsuario();
      const empresa = authService.getEmpresa();

      // VALIDAÇÃO CRÍTICA: Verificar dados do usuário e empresa
      // console.log('[SECURITY-CHECK] Validação de acesso:', {
        // userExists: !!usuario,
        // empresaExists: !!empresa,
        // codEmpresa: empresa?.id,
        // usuarioId: usuario?.id,
        // timestamp: new Date().toISOString(),
        // endpoint: '/configuracoes',
        // ambiente: window.location.hostname
      // });

      if (!usuario || !empresa) {
        throw new Error('ACESSO NEGADO: Dados de usuário ou empresa inválidos');
      }

      if (!empresa.id || typeof empresa.id !== 'number' || empresa.id <= 0) {
        throw new Error('ACESSO NEGADO: Código da empresa inválido ou ausente');
      }

      // Carregar dados completos da empresa do backend
      // console.log('[Configuracoes] STEP 1: Iniciando carregamento dos dados da empresa do backend...');
      
      try {
        // console.log('[Configuracoes] STEP 2: Chamando empresasService.buscarMinhaEmpresa()...');
        // console.log('[Configuracoes] STEP 2.1: empresasService disponível?', !!empresasService);
        // console.log('[Configuracoes] STEP 2.2: método buscarMinhaEmpresa disponível?', typeof empresasService.buscarMinhaEmpresa);
        
        const resultadoEmpresa = await empresasService.buscarMinhaEmpresa();
        
        // console.log('[Configuracoes] STEP 3: Resposta recebida do empresasService:', {
          // sucesso: resultadoEmpresa.sucesso,
          // mensagem: resultadoEmpresa.mensagem,
          // temDados: !!resultadoEmpresa.dados,
          // timestamp: new Date().toISOString()
        // });

        if (resultadoEmpresa.sucesso && resultadoEmpresa.dados) {
          setDadosEmpresa(resultadoEmpresa.dados);
          // console.log('[Configuracoes] Dados da empresa carregados do backend:', {
            // empresaId: resultadoEmpresa.dados.id,
            // empresaNome: resultadoEmpresa.dados.nome,
            // timestamp: new Date().toISOString()
          // });
        } else {
          // Fallback: usar dados do authService se não conseguir carregar do backend
          // console.warn('[Configuracoes] Fallback: usando dados do authService:', resultadoEmpresa.mensagem);
          setDadosEmpresa(prev => ({
            ...prev,
            id: empresa.id,
            nome: empresa.nome,
            email: '',
            ativo: true
          }));
        }
      } catch (empresaError) {
        console.error('[Configuracoes] Erro específico ao carregar dados da empresa:', {
          error: empresaError,
          message: empresaError instanceof Error ? empresaError.message : 'Erro desconhecido',
          timestamp: new Date().toISOString()
        });
        
        // Fallback: usar dados básicos do authService
        setDadosEmpresa(prev => ({
          ...prev,
          id: empresa.id,
          nome: empresa.nome,
          email: '',
          ativo: true
        }));
      }

      // Carregar status da conexão Google
      try {
        const statusGoogle = await gmailService.verificarConexao();
        if (statusGoogle.sucesso && statusGoogle.dados) {
          setContaGoogleConectada(true);
          setDadosContaGoogle({
            email: statusGoogle.dados.email,
            nome: statusGoogle.dados.nome || statusGoogle.dados.email,
            conectadoEm: statusGoogle.dados.conectadoEm
          });
          setIntegracaoGoogle(prev => ({
            ...prev,
            emailHabilitado: true,
            agendaHabilitada: true, // Habilitar Google Calendar automaticamente
            emailRemetente: statusGoogle.dados.email,
            nomeRemetente: statusGoogle.dados.nome || statusGoogle.dados.email
          }));
        } else {
          setContaGoogleConectada(false);
          setDadosContaGoogle(null);
        }
      } catch (error) {
        // console.log('[Configuracoes] Conta Google não conectada ainda:', error);
        setContaGoogleConectada(false);
        setDadosContaGoogle(null);
      }

      // TODO: Implementar carregamento de configurações específicas
      // const configuracoes = await configuracaoService.buscarPorEmpresa(empresa.id);
      // setHorariosTrabalho(configuracoes.horarios || horariosTrabalho);
      // setConfiguracaoTema(configuracoes.tema || configuracaoTema);
      // setIntegracaoGoogle(configuracoes.google || integracaoGoogle);

      // console.log('[Configuracoes] Dados carregados com sucesso:', {
        // empresaId: empresa.id,
        // empresaNome: empresa.nome,
        // usuario: usuario.nome,
        // timestamp: new Date().toISOString()
      // });

      // Carregar templates de notificação
      try {
        // console.log('[Configuracoes] Carregando templates de notificação...');
        const templatesResult = await templatesNotificacaoService.buscarTemplates();
        
        if (templatesResult.sucesso && templatesResult.dados) {
          // console.log('[Configuracoes] Templates carregados:', {
            // temEmailConfirmacao: !!templatesResult.dados.emailConfirmacaoHtml,
            // temEmailCancelamento: !!templatesResult.dados.emailCancelamentoHtml,
            // temCalendarTitulo: !!templatesResult.dados.calendarTituloTemplate
          // });
          
          setTemplatesNotificacao(prev => ({
            ...prev,
            ...templatesResult.dados,
            codEmpresa: prev.codEmpresa ?? empresa.id,
            usuarioId: prev.usuarioId ?? usuario.id
          }));
        } else {
          // console.log('[Configuracoes] Nenhum template personalizado encontrado, usando padrões');
          setTemplatesNotificacao(prev => ({
            ...prev,
            codEmpresa: empresa.id,
            usuarioId: usuario.id
          }));
        }
      } catch (error) {
        console.error('[Configuracoes] Erro ao carregar templates:', error);
        // Não é um erro crítico, usar templates padrão
        setTemplatesNotificacao(prev => ({
          ...prev,
          codEmpresa: empresa.id,
          usuarioId: usuario.id
        }));
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      console.error('[Configuracoes] Erro ao carregar configurações:', {
        error,
        errorMessage,
        timestamp: new Date().toISOString(),
        stack: error instanceof Error ? error.stack : undefined,
        usuario: authService.getUsuario()?.id,
        empresa: authService.getEmpresa()?.id
      });

      setErro(`Não foi possível carregar as configurações: ${errorMessage}`);
      
      // Se erro de autenticação, redirecionar
      if (errorMessage.includes('ACESSO NEGADO')) {
        setTimeout(() => navigate('/login'), 2000);
      }
    } finally {
      setCarregando(false);
    }
  }, [navigate]);

  // Carregar disponibilidades do banco de dados
  const carregarDisponibilidades = useCallback(async () => {
    if (!usuarioId) return;

    try {
      setCarregandoDisponibilidades(true);
      
      // console.log('[DisponibilidadesPage] Carregando disponibilidades:', {
        // usuarioId,
        // timestamp: new Date().toISOString()
      // });

      const resultado = await disponibilidadesService.listar(usuarioId);

      if (resultado.sucesso && resultado.dados) {
        // Converter disponibilidades para o formato usado pela interface
        const horarios: HorarioTrabalho[] = [];
        
        // Separar horários de trabalho e intervalos
        const horariosTrabalho = resultado.dados.filter((d: Disponibilidade) => 
          d.tipo === 'trabalho' || !d.tipo // Compatibilidade com registros antigos
        );
        const intervalos = resultado.dados.filter((d: Disponibilidade) => 
          d.tipo === 'intervalo'
        );
        
        // Inicializar todos os dias da semana
        for (let dia = 0; dia <= 6; dia++) {
          const disponibilidadeDia = horariosTrabalho.find((d: Disponibilidade) => d.dia_semana === dia);
          const intervalosDia = intervalos.filter((d: Disponibilidade) => d.dia_semana === dia);
          
          if (disponibilidadeDia) {
            // CORREÇÃO CRÍTICA: Garantir formato HH:MM ao carregar do banco
            const formatarHorario = (hora: string) => {
              if (!hora) return '08:00';
              if (/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(hora)) {
                return hora;
              }
              if (/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/.test(hora)) {
                return hora.substring(0, 5);
              }
              return '08:00';
            };

            horarios.push({
              id: disponibilidadeDia.id?.toString() || `${dia}`,
              diaSemana: dia,
              ativo: disponibilidadeDia.ativo,
              horaInicio: formatarHorario(disponibilidadeDia.hora_inicio),
              horaFim: formatarHorario(disponibilidadeDia.hora_fim),
              intervalo_minutos: disponibilidadeDia.intervalo_minutos,
              observacoes: disponibilidadeDia.observacoes,
              usuario_id: disponibilidadeDia.usuario_id,
              intervalos: intervalosDia.map((intervalo: Disponibilidade) => ({
                id: intervalo.id?.toString() || `intervalo-${Date.now()}`,
                horaInicio: formatarHorario(intervalo.hora_inicio),
                horaFim: formatarHorario(intervalo.hora_fim),
                descricao: intervalo.observacoes || 'Intervalo'
              }))
            });
          } else {
            // Dia sem disponibilidade configurada
            const formatarHorario = (hora: string) => {
              if (!hora) return '08:00';
              if (/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(hora)) {
                return hora;
              }
              if (/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/.test(hora)) {
                return hora.substring(0, 5);
              }
              return '08:00';
            };

            horarios.push({
              id: `novo-${dia}`,
              diaSemana: dia,
              ativo: false,
              horaInicio: '08:00',
              horaFim: '18:00',
              intervalo_minutos: 60,
              usuario_id: usuarioId,
              intervalos: intervalosDia.map((intervalo: Disponibilidade) => ({
                id: intervalo.id?.toString() || `intervalo-${Date.now()}`,
                horaInicio: formatarHorario(intervalo.hora_inicio),
                horaFim: formatarHorario(intervalo.hora_fim),
                descricao: intervalo.observacoes || 'Intervalo'
              }))
            });
          }
        }
        
        setHorariosTrabalho(horarios);
        
        // console.log('[DisponibilidadesPage] Disponibilidades carregadas:', {
          // total: resultado.dados.length,
          // timestamp: new Date().toISOString()
        // });
      } else {
        // console.warn('[DisponibilidadesPage] Nenhuma disponibilidade encontrada');
        // Manter horários padrão
      }

    } catch (error) {
      console.error('[DisponibilidadesPage] Erro ao carregar disponibilidades:', {
        error: error instanceof Error ? error.message : error,
        usuarioId,
        timestamp: new Date().toISOString()
      });
    } finally {
      setCarregandoDisponibilidades(false);
    }
  }, [usuarioId]);

  // Carregar preferências de alertas
  const carregarPreferenciasAlertas = useCallback(async () => {
    try {
      setCarregandoPreferenciasAlertas(true);
      const preferencias = await notificationsService.buscarPreferencias();
      setPreferenciasAlertas(preferencias);
    } catch (error) {
      console.error('[Configuracoes] Erro ao carregar preferências de alertas:', error);
      // Manter valores padrão em caso de erro
    } finally {
      setCarregandoPreferenciasAlertas(false);
    }
  }, []);

  // Salvar preferências de alertas
  const salvarPreferenciasAlertas = useCallback(async () => {
    try {
      setSalvandoPreferenciasAlertas(true);
      await notificationsService.atualizarPreferencias(preferenciasAlertas);
      setSucesso('Preferências de alertas salvas com sucesso!');
      setTimeout(() => setSucesso(null), 3000);
    } catch (error) {
      console.error('[Configuracoes] Erro ao salvar preferências de alertas:', error);
      setErro('Erro ao salvar preferências de alertas');
      setTimeout(() => setErro(null), 5000);
    } finally {
      setSalvandoPreferenciasAlertas(false);
    }
  }, [preferenciasAlertas]);

  // Handler para mudanças nos dados da empresa
  const handleEmpresaChange = useCallback((field: keyof DadosEmpresa, value: any) => {
    setDadosEmpresa(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Handler para mudanças nos horários de trabalho - APENAS ESTADO LOCAL
  const handleHorarioChange = useCallback((diaIndex: number, field: keyof HorarioTrabalho, value: any) => {
    // console.log('[Configuracoes] Atualizando estado local do horário:', { diaIndex, field, value });
    
    // Atualizar APENAS estado local - SEM salvar no banco
    const novosHorarios = [...horariosTrabalho];
    novosHorarios[diaIndex] = { ...novosHorarios[diaIndex], [field]: value };
    setHorariosTrabalho(novosHorarios);
    
    // console.log('[Configuracoes] Estado local atualizado. Use "Salvar Configurações" para persistir.');
  }, [horariosTrabalho]);

  // Adicionar intervalo - APENAS NA UI (não salva no banco ainda)
  const adicionarIntervalo = useCallback((diaIndex: number) => {
    try {
      // console.log('[Configuracoes] Adicionando intervalo na UI para dia:', diaIndex);

      // Gerar ID temporário para o novo intervalo
      const novoIntervaloId = `temp_${Date.now()}_${Math.random()}`;
      
      // Criar novo intervalo temporário
      const novoIntervalo = {
        id: novoIntervaloId,
        horaInicio: '12:00',
        horaFim: '13:00',
        descricao: 'Intervalo',
        temporario: true // Flag para identificar que ainda não foi salvo
      };

      // Atualizar estado local apenas
      const novosHorarios = [...horariosTrabalho];
      novosHorarios[diaIndex] = {
        ...novosHorarios[diaIndex],
        intervalos: [...novosHorarios[diaIndex].intervalos, novoIntervalo]
      };
      
      setHorariosTrabalho(novosHorarios);
      
      // console.log('[Configuracoes] Intervalo adicionado na UI. Use "Salvar Configurações" para persistir.');

    } catch (error) {
      console.error('[Configuracoes] Erro ao adicionar intervalo na UI:', error);
      setErro('Erro ao adicionar intervalo');
    }
  }, [horariosTrabalho]);

  // Remover intervalo - EXCLUIR DIRETO DO BANCO E UI
  const removerIntervalo = useCallback(async (diaIndex: number, intervaloId: string) => {
    try {
      // console.log('[Configuracoes] Removendo intervalo:', { diaIndex, intervaloId });

      // Verificar se é um ID temporário (ainda não salvo no banco)
      if (intervaloId.startsWith('temp_')) {
        // console.log('[Configuracoes] Removendo intervalo temporário da UI apenas');
        // Apenas remover da UI
        setHorariosTrabalho(prev => prev.map((horario, index) => 
          index === diaIndex 
            ? { ...horario, intervalos: horario.intervalos.filter(i => i.id !== intervaloId) }
            : horario
        ));
        setSucesso('Intervalo removido da lista');
        return;
      }

      // ID numérico = registro salvo no banco, excluir definitivamente
      const id = parseInt(intervaloId);
      if (isNaN(id)) {
        setErro('ID do intervalo inválido');
        return;
      }

      // console.log('[Configuracoes] Excluindo intervalo do banco de dados:', id);

      // Excluir DEFINITIVAMENTE do banco via API
      const resultado = await disponibilidadesService.excluir(id);

      if (resultado.sucesso) {
        // console.log('[Configuracoes] Intervalo excluído definitivamente do banco');
        
        // Remover da UI também
        setHorariosTrabalho(prev => prev.map((horario, index) => 
          index === diaIndex 
            ? { ...horario, intervalos: horario.intervalos.filter(i => i.id !== intervaloId) }
            : horario
        ));
        
        setSucesso('Intervalo excluído com sucesso!');
      } else {
        console.error('[Configuracoes] Erro ao excluir intervalo:', resultado.mensagem);
        setErro(resultado.mensagem);
      }

    } catch (error) {
      console.error('[Configuracoes] Erro ao remover intervalo:', error);
      setErro('Erro ao remover intervalo');
    }
  }, []);

  // Handler para mudanças na integração Google
  const handleGoogleChange = useCallback((field: keyof IntegracaoGoogle, value: any) => {
    setIntegracaoGoogle(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Salvar configurações
  const salvarConfiguracoes = useCallback(async () => {
    try {
      setSalvando(true);
      setErro(null);

      // VALIDAÇÃO CRÍTICA: Verificar autenticação antes de salvar
      const token = sessionStorage.getItem('token');
      if (!token) {
        console.error('[SECURITY-CHECK] Token perdido durante operação de salvamento');
        navigate('/login');
        return;
      }

      const usuario = authService.getUsuario();
      const empresa = authService.getEmpresa();

      if (!usuario || !empresa) {
        throw new Error('ACESSO NEGADO: Sessão inválida para salvamento');
      }

      // console.log('[Configuracoes] Iniciando salvamento:', {
        // aba: abaSelecionada,
        // empresaId: empresa.id,
        // empresaNome: empresa.nome,
        // usuarioId: usuario.id,
        // horariosAtivos: horariosTrabalho.filter(h => h.ativo).length,
        // googleEmail: integracaoGoogle.emailHabilitado,
        // googleAgenda: integracaoGoogle.agendaHabilitada,
        // timestamp: new Date().toISOString()
      // });

      // Validar dados antes de enviar
      if (!dadosEmpresa.nome || !dadosEmpresa.email) {
        throw new Error('Campos obrigatórios não preenchidos: Nome e E-mail são obrigatórios');
      }

      // Salvar dados da empresa se estamos na aba empresa
      if (abaSelecionada === 'empresa') {
        const resultadoEmpresa = await empresasService.atualizarEmpresa({
          nome: dadosEmpresa.nome,
          nome_fantasia: dadosEmpresa.nome_fantasia ?? '',
          cnpj: dadosEmpresa.cnpj ?? '',
          email: dadosEmpresa.email,
          telefone: dadosEmpresa.telefone ?? '',
          endereco: dadosEmpresa.endereco ?? '',
          cep: dadosEmpresa.cep ?? '',
          cidade: dadosEmpresa.cidade ?? '',
          estado: dadosEmpresa.estado ?? ''
        });
        
        if (!resultadoEmpresa.sucesso) {
          throw new Error(resultadoEmpresa.mensagem);
        }

        // console.log('[Configuracoes] Dados da empresa salvos com sucesso');
      }

      // Salvar horários de trabalho e intervalos se estamos na aba horários
      if (abaSelecionada === 'horarios') {
        // console.log('[Configuracoes] Salvando horários de trabalho e intervalos...');
        
        // Configurar tabela se necessário (primeira vez)
        try {
          await disponibilidadesService.configurarTabelaIntervalos();
        } catch (error) {
          // console.warn('[Configuracoes] Configuração da tabela falhou, continuando...', error);
        }

        // 1. Salvar HORÁRIOS DE TRABALHO básicos (ativo/inativo, horário início/fim)
        if (usuarioId) {
          for (const horario of horariosTrabalho) {
            // CORREÇÃO CRÍTICA: Garantir formato HH:MM (sem segundos)
            const formatarHorario = (hora: string) => {
              if (!hora) return '08:00';
              // Se já está no formato HH:MM, manter
              if (/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(hora)) {
                return hora;
              }
              // Se tem segundos HH:MM:SS, remover
              if (/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/.test(hora)) {
                return hora.substring(0, 5);
              }
              // Fallback seguro
              return '08:00';
            };

            const dadosDisponibilidade = {
              usuario_id: usuarioId,
              dia_semana: horario.diaSemana,
              hora_inicio: formatarHorario(horario.horaInicio),
              hora_fim: formatarHorario(horario.horaFim),
              tipo: 'trabalho' as 'trabalho' | 'intervalo',
              intervalo_minutos: horario.intervalo_minutos || 60,
              ativo: horario.ativo,
              observacoes: horario.observacoes ?? ''
            };

            // DEBUGGING: Log detalhado dos dados sendo enviados
            // console.log('[Configuracoes] DADOS ENVIADOS PARA API:', {
              // operacao: horario.id && !horario.id.startsWith('novo-') ? 'UPDATE' : 'CREATE',
              // id: horario.id,
              // idParseado: horario.id && !horario.id.startsWith('novo-') ? parseInt(horario.id) : 'N/A',
              // dadosDisponibilidade,
              // diaSemana: horario.diaSemana,
              // horaInicioOriginal: horario.horaInicio,
              // horaFimOriginal: horario.horaFim,
              // observacoesOriginal: horario.observacoes,
              // observacoesEnviadas: dadosDisponibilidade.observacoes,
              // timestamp: new Date().toISOString()
            // });

            let resultado;
            
            if (horario.id && !horario.id.startsWith('novo-')) {
              // Atualizar existente
              // console.log('[Configuracoes] EXECUTANDO ATUALIZAÇÃO - ID:', parseInt(horario.id));
              resultado = await disponibilidadesService.atualizar(parseInt(horario.id), dadosDisponibilidade);
            } else {
              // Criar novo
              // console.log('[Configuracoes] EXECUTANDO CRIAÇÃO');
              resultado = await disponibilidadesService.criar(dadosDisponibilidade);
            }

            if (!resultado.sucesso) {
              throw new Error(`Erro ao salvar horário de ${['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'][horario.diaSemana]}: ${resultado.mensagem}`);
            }
          }
        }

        // 2. Salvar INTERVALOS (novos E existentes)
        for (const horario of horariosTrabalho) {
          if (horario.intervalos.length > 0 && usuarioId) {
            // console.log('[Configuracoes] Processando intervalos para dia', horario.diaSemana, ':', horario.intervalos);
            
            // Separar intervalos novos/temporários dos existentes
            const intervalosTemporarios = horario.intervalos.filter(i => i.temporario || i.id.toString().startsWith('temp_'));
            const intervalosExistentes = horario.intervalos.filter(i => !i.temporario && !i.id.toString().startsWith('temp_'));
            
            // CORREÇÃO CRÍTICA: Garantir formato HH:MM para intervalos
            const formatarHorario = (hora: string) => {
              if (!hora) return '12:00';
              if (/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(hora)) {
                return hora;
              }
              if (/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/.test(hora)) {
                return hora.substring(0, 5);
              }
              return '12:00';
            };

            // A) ATUALIZAR intervalos existentes
            for (const intervalo of intervalosExistentes) {
              // console.log('[Configuracoes] ATUALIZANDO intervalo existente:', {
                // id: intervalo.id,
                // descricao: intervalo.descricao,
                // horaInicio: intervalo.horaInicio,
                // horaFim: intervalo.horaFim,
                // timestamp: new Date().toISOString()
              // });

              const dadosIntervalo = {
                dia_semana: horario.diaSemana,
                hora_inicio: formatarHorario(intervalo.horaInicio),
                hora_fim: formatarHorario(intervalo.horaFim),
                tipo: 'intervalo' as 'trabalho' | 'intervalo',
                intervalo_minutos: 0,
                ativo: true,
                observacoes: intervalo.descricao || 'Intervalo'
              };

              const resultado = await disponibilidadesService.atualizar(parseInt(intervalo.id.toString()), dadosIntervalo);

              if (!resultado.sucesso) {
                throw new Error(`Erro ao atualizar intervalo: ${resultado.mensagem}`);
              }

              // console.log('[Configuracoes] Intervalo atualizado com sucesso:', {
                // id: intervalo.id,
                // resultado: resultado.mensagem
              // });
            }

            // B) CRIAR intervalos novos/temporários
            for (const intervalo of intervalosTemporarios) {
              // console.log('[Configuracoes] CRIANDO intervalo temporário:', {
                // id: intervalo.id,
                // descricao: intervalo.descricao,
                // horaInicio: intervalo.horaInicio,
                // horaFim: intervalo.horaFim,
                // timestamp: new Date().toISOString()
              // });

              const resultado = await disponibilidadesService.criarIntervalo({
                usuario_id: usuarioId,
                dia_semana: horario.diaSemana,
                hora_inicio: formatarHorario(intervalo.horaInicio),
                hora_fim: formatarHorario(intervalo.horaFim),
                observacoes: intervalo.descricao || 'Intervalo'
              });

              if (!resultado.sucesso) {
                throw new Error(`Erro ao salvar intervalo: ${resultado.mensagem}`);
              }

              // console.log('[Configuracoes] Intervalo criado com sucesso:', {
                // id: intervalo.id,
                // resultado: resultado.mensagem
              // });
            }
          }
        }

        // Recarregar disponibilidades para atualizar IDs e remover flags temporárias
        await carregarDisponibilidades();
        
        // console.log('[Configuracoes] Horários e intervalos salvos com sucesso');
      }

      // Salvar templates de notificação se estamos na aba notificações
      if (abaSelecionada === 'notificacoes') {
        try {
          setSalvandoTemplates(true);
          // console.log('[Configuracoes] Salvando templates de notificação...');
          
          const templatesParaSalvar = {
            ...templatesNotificacao,
            codEmpresa: empresa.id,
            usuarioId: usuario.id
          };
          
          const resultadoTemplates = await templatesNotificacaoService.salvarTemplates(templatesParaSalvar);
          
          if (!resultadoTemplates.sucesso) {
            throw new Error(`Erro ao salvar templates: ${resultadoTemplates.mensagem}`);
          }
          
          // console.log('[Configuracoes] Templates salvos com sucesso');
          
          // Atualizar estado com dados salvos
          if (resultadoTemplates.dados) {
            setTemplatesNotificacao(prev => ({
              ...prev,
              ...resultadoTemplates.dados
            }));
          }
          
        } catch (templateError) {
          console.error('[Configuracoes] Erro específico ao salvar templates:', templateError);
          throw templateError;
        } finally {
          setSalvandoTemplates(false);
        }
      }

      // TODO: Implementar salvamento das outras abas conforme cada funcionalidade for desenvolvida
      // Quando implementar, seguir o padrão:
      /*
      const resultadoHorarios = await configuracaoService.salvarHorarios(empresa.id, horariosTrabalho);
      if (!resultadoHorarios.sucesso) {
        throw new Error(resultadoHorarios.mensagem);
      }

      const resultadoTema = await configuracaoService.salvarTema(empresa.id, configuracaoTema);
      if (!resultadoTema.sucesso) {
        throw new Error(resultadoTema.mensagem);
      }

      const resultadoGoogle = await configuracaoService.salvarIntegracaoGoogle(empresa.id, integracaoGoogle);
      if (!resultadoGoogle.sucesso) {
        throw new Error(resultadoGoogle.mensagem);
      }
      */

      // console.log('[Configuracoes] Configurações salvas com sucesso:', {
        // empresaId: empresa.id,
        // timestamp: new Date().toISOString(),
        // operacao: 'salvar_configuracoes'
      // });

      setSucesso('Configurações salvas com sucesso!');
      setTimeout(() => setSucesso(null), 3000);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      console.error('[Configuracoes] Erro ao salvar configurações:', {
        error,
        errorMessage,
        timestamp: new Date().toISOString(),
        stack: error instanceof Error ? error.stack : undefined,
        aba: abaSelecionada,
        usuario: authService.getUsuario()?.id,
        empresa: authService.getEmpresa()?.id
      });

      setErro(`Erro ao salvar configurações: ${errorMessage}`);
      
      // Se erro de autenticação, redirecionar
      if (errorMessage.includes('ACESSO NEGADO')) {
        setTimeout(() => navigate('/login'), 2000);
      }
    } finally {
      setSalvando(false);
    }
  }, [abaSelecionada, dadosEmpresa, horariosTrabalho, integracaoGoogle, templatesNotificacao, navigate]);

  // Testar conexão Google
  const testarConexaoGoogle = useCallback(async () => {
    try {
      setSalvando(true);

      // VALIDAÇÃO CRÍTICA: Verificar autenticação
      const token = sessionStorage.getItem('token');
      if (!token) {
        console.error('[SECURITY-CHECK] Token perdido durante teste de conexão');
        navigate('/login');
        return;
      }

      const empresa = authService.getEmpresa();
      if (!empresa) {
        throw new Error('ACESSO NEGADO: Empresa não identificada para teste');
      }
      
      // console.log('[Configuracoes] Testando conexão Google:', {
        // empresaId: empresa.id,
        // timestamp: new Date().toISOString()
      // });

      const resultado = await gmailService.verificarConexao();
      if (resultado.sucesso) {
        alert('Conexão com Gmail está funcionando perfeitamente!');
      } else {
        alert(`Erro na conexão: ${resultado.mensagem}`);
      }
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      console.error('[Configuracoes] Erro no teste de conexão Google:', {
        error,
        errorMessage,
        timestamp: new Date().toISOString(),
        stack: error instanceof Error ? error.stack : undefined,
        empresa: authService.getEmpresa()?.id
      });

      alert(`Erro ao testar conexão com Google: ${errorMessage}`);
      
      if (errorMessage.includes('ACESSO NEGADO')) {
        setTimeout(() => navigate('/login'), 2000);
      }
    } finally {
      setSalvando(false);
    }
  }, [navigate]);

  // Conectar conta Google
  const conectarContaGoogle = useCallback(async () => {
    try {
      setConectandoGoogle(true);

      // VALIDAÇÃO CRÍTICA: Verificar autenticação
      const token = sessionStorage.getItem('token');
      if (!token) {
        console.error('[SECURITY-CHECK] Token perdido durante conexão');
        navigate('/login');
        return;
      }

      const empresa = authService.getEmpresa();
      if (!empresa) {
        throw new Error('ACESSO NEGADO: Empresa não identificada para conexão');
      }

      // console.log('[Configuracoes] Iniciando conexão com conta Google:', {
        // empresaId: empresa.id,
        // timestamp: new Date().toISOString()
      // });

      const resultado = await gmailService.conectarConta();
      if (!resultado.sucesso) {
        throw new Error(resultado.mensagem);
      }

      // O redirecionamento será feito automaticamente pelo serviço
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      console.error('[Configuracoes] Erro ao conectar conta Google:', {
        error,
        errorMessage,
        timestamp: new Date().toISOString(),
        stack: error instanceof Error ? error.stack : undefined,
        empresa: authService.getEmpresa()?.id
      });

      alert(`Erro ao conectar conta Google: ${errorMessage}`);
      
      if (errorMessage.includes('ACESSO NEGADO')) {
        setTimeout(() => navigate('/login'), 2000);
      }
    } finally {
      setConectandoGoogle(false);
    }
  }, [navigate]);

  // Enviar email de teste
  const enviarEmailTeste = useCallback(async () => {
    try {
      setEnviandoTeste(true);
      setErro(null);

      // VALIDAÇÃO CRÍTICA: Verificar autenticação
      const token = sessionStorage.getItem('token');
      if (!token) {
        console.error('[SECURITY-CHECK] Token perdido durante envio de teste');
        navigate('/login');
        return;
      }

      const empresa = authService.getEmpresa();
      if (!empresa) {
        throw new Error('ACESSO NEGADO: Empresa não identificada para envio');
      }

      // Validar campos do email de teste
      if (!emailTeste.destinatario || !emailTeste.assunto || !emailTeste.mensagem) {
        throw new Error('Preencha todos os campos do email de teste');
      }

      // Validar email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailTeste.destinatario)) {
        throw new Error('Digite um email válido para o destinatário');
      }

      // console.log('[Configuracoes] Enviando email de teste:', {
        // empresaId: empresa.id,
        // destinatario: emailTeste.destinatario,
        // assunto: emailTeste.assunto,
        // timestamp: new Date().toISOString()
      // });

      const resultado = await gmailService.enviarEmail({
        para: emailTeste.destinatario,
        assunto: emailTeste.assunto,
        corpo: emailTeste.mensagem
      });

      if (!resultado.sucesso) {
        throw new Error(resultado.mensagem);
      }

      setSucesso('Email de teste enviado com sucesso! Verifique a caixa de entrada do destinatário.');
      // console.log('[Configuracoes] Email de teste enviado:', {
        // empresaId: empresa.id,
        // destinatario: emailTeste.destinatario,
        // resultado: resultado.dados,
        // timestamp: new Date().toISOString()
      // });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      console.error('[Configuracoes] Erro ao enviar email de teste:', {
        error,
        errorMessage,
        timestamp: new Date().toISOString(),
        empresa: authService.getEmpresa()?.id
      });

      setErro(`Erro ao enviar email de teste: ${errorMessage}`);
      
      if (errorMessage.includes('ACESSO NEGADO')) {
        setTimeout(() => navigate('/login'), 2000);
      }
    } finally {
      setEnviandoTeste(false);
    }
  }, [emailTeste, navigate]);

  // Salvar assinatura de email
  const salvarAssinatura = useCallback(async () => {
    try {
      setSalvando(true);
      setErro(null);

      const empresa = authService.getEmpresa();
      if (!empresa) {
        throw new Error('ACESSO NEGADO: Empresa não identificada');
      }

      // console.log('[Configuracoes] Salvando assinatura de email:', {
        // empresaId: empresa.id,
        // assinatura: integracaoGoogle.assinaturaEmail?.substring(0, 50) + '...',
        // timestamp: new Date().toISOString()
      // });

      const resultado = await gmailService.salvarConfiguracao({
        email_remetente: integracaoGoogle.emailRemetente,
        nome_remetente: integracaoGoogle.nomeRemetente,
        assinatura_email: integracaoGoogle.assinaturaEmail,
        ativo: true
      });

      if (!resultado.sucesso) {
        throw new Error(resultado.mensagem);
      }

      setSucesso('Assinatura de email salva com sucesso!');
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      console.error('[Configuracoes] Erro ao salvar assinatura:', {
        error,
        errorMessage,
        timestamp: new Date().toISOString()
      });

      setErro(`Erro ao salvar assinatura: ${errorMessage}`);
    } finally {
      setSalvando(false);
    }
  }, [integracaoGoogle.assinaturaEmail, integracaoGoogle.emailRemetente, integracaoGoogle.nomeRemetente]);

  // Desconectar conta Google
  const desconectarContaGoogle = useCallback(async () => {
    if (!confirm('Tem certeza que deseja desconectar sua conta Google? Isso impedirá o envio de emails.')) {
      return;
    }

    try {
      setConectandoGoogle(true);

      const resultado = await gmailService.desconectarConta();
      if (!resultado.sucesso) {
        throw new Error(resultado.mensagem);
      }

      // Atualizar estado local
      setContaGoogleConectada(false);
      setDadosContaGoogle(null);
      setIntegracaoGoogle(prev => ({
        ...prev,
        emailHabilitado: false,
        emailRemetente: '',
        nomeRemetente: ''
      }));

      alert('Conta Google desconectada com sucesso!');
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      console.error('[Configuracoes] Erro ao desconectar conta Google:', {
        error,
        errorMessage,
        timestamp: new Date().toISOString()
      });

      alert(`Erro ao desconectar conta Google: ${errorMessage}`);
    } finally {
      setConectandoGoogle(false);
    }
  }, []);

  // Reconectar com todos os escopos (Gmail + Calendar)
  const reconectarComTodosEscopos = useCallback(async () => {
    if (!confirm('Isso irá reconectar sua conta Google e solicitar permissões para Gmail E Google Calendar. Continuar?')) {
      return;
    }

    try {
      setConectandoGoogle(true);

      // console.log('[Configuracoes] Iniciando reconexão com todos os escopos:', {
        // timestamp: new Date().toISOString()
      // });

      const resultado = await gmailService.reconectarComTodosEscopos();
      
      if (!resultado.sucesso) {
        throw new Error(resultado.mensagem);
      }

      // O redirecionamento será feito automaticamente pelo serviço
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      console.error('[Configuracoes] Erro ao reconectar com todos os escopos:', {
        error,
        errorMessage,
        timestamp: new Date().toISOString()
      });

      alert(`Erro ao reconectar: ${errorMessage}`);
    } finally {
      setConectandoGoogle(false);
    }
  }, []);

  // Recarregar tokens do Calendar após mudança de conta
  const recarregarTokensCalendar = useCallback(async () => {
    try {
      setSalvando(true);
      setErro(null);

      // console.log('[Configuracoes] Forçando recarga dos tokens do Calendar:', {
        // timestamp: new Date().toISOString()
      // });

      const resultado = await calendarService.recarregarTokens();
      
      if (resultado.sucesso) {
        setSucesso('Tokens do Calendar atualizados! O calendar agora está sincronizado com a nova conta.');
        
        // Recarregar a página para atualizar o estado
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        throw new Error(resultado.mensagem);
      }
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      console.error('[Configuracoes] Erro ao recarregar tokens do Calendar:', {
        error,
        errorMessage,
        timestamp: new Date().toISOString()
      });

      setErro(`Erro ao atualizar tokens do Calendar: ${errorMessage}`);
    } finally {
      setSalvando(false);
    }
  }, []);

  // ==================== FUNÇÕES GOOGLE CALENDAR ====================

  // Carregar calendários do usuário
  const carregarCalendarios = useCallback(async () => {
    try {
      setCarregandoCalendarios(true);
      setErro(null);

      // console.log('[Configuracoes] Carregando calendários do Google:', {
        // timestamp: new Date().toISOString()
      // });

      const resultado = await calendarService.listarCalendarios();
      
      if (resultado.sucesso && resultado.dados) {
        setCalendarios(resultado.dados);
        
        // Selecionar calendário primário por padrão
        const calendarioPrimario = resultado.dados.find((cal: CalendarInfo) => cal.primary);
        if (calendarioPrimario) {
          setCalendarioSelecionado(calendarioPrimario.id);
          setIntegracaoGoogle(prev => ({
            ...prev,
            agendaId: calendarioPrimario.id,
            agendaHabilitada: true
          }));
        }

        // console.log('[Configuracoes] Calendários carregados:', {
          // quantidade: resultado.dados.length,
          // calendarioPrimario: calendarioPrimario?.summary
        // });
      } else {
        // Verificar se é erro de token expirado
        if (resultado.erro === 'TOKEN_EXPIRED') {
          // console.warn('[Configuracoes] Token Google expirado - orientando reconexão');
          setErro(`⚠️ ${resultado.mensagem}\n\n🔄 Solução: Vá para a seção "Conexão Gmail" acima e clique em "Reconectar com todos os escopos" para renovar a autorização.`);
        } else {
          throw new Error(resultado.mensagem || 'Erro ao carregar calendários');
        }
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      console.error('[Configuracoes] Erro ao carregar calendários:', {
        error,
        errorMessage,
        timestamp: new Date().toISOString()
      });

      setErro(`Erro ao carregar calendários: ${errorMessage}`);
    } finally {
      setCarregandoCalendarios(false);
    }
  }, []);

  // Criar evento de teste no Calendar
  const criarEventoTeste = useCallback(async () => {
    try {
      setCriandoEventoTeste(true);
      setErro(null);

      // VALIDAÇÃO CRÍTICA: Verificar autenticação
      const token = sessionStorage.getItem('token');
      if (!token) {
        console.error('[SECURITY-CHECK] Token perdido durante criação de evento');
        navigate('/login');
        return;
      }

      const empresa = authService.getEmpresa();
      if (!empresa) {
        throw new Error('ACESSO NEGADO: Empresa não identificada para criação de evento');
      }

      if (!calendarioSelecionado) {
        throw new Error('Selecione um calendário para criar o evento de teste');
      }

      // Preparar datas para o evento
      const agora = new Date();
      const dataInicio = eventoTeste.dataInicio ? 
        new Date(`${eventoTeste.dataInicio}T${eventoTeste.horaInicio}:00`) : 
        new Date(agora.getTime() + (60 * 60 * 1000)); // 1 hora a partir de agora

      const dataFim = eventoTeste.dataFim ? 
        new Date(`${eventoTeste.dataFim}T${eventoTeste.horaFim}:00`) : 
        new Date(dataInicio.getTime() + (60 * 60 * 1000)); // 1 hora de duração

      const evento: CalendarEvent = {
        summary: eventoTeste.titulo,
        description: eventoTeste.descricao,
        start: {
          dateTime: dataInicio.toISOString(),
          timeZone: 'America/Sao_Paulo'
        },
        end: {
          dateTime: dataFim.toISOString(),
          timeZone: 'America/Sao_Paulo'
        },
        reminders: {
          useDefault: false,
          overrides: [
            { method: 'popup', minutes: 5 }
          ]
        },
        status: 'confirmed',
        visibility: 'private'
      };

      // console.log('[Configuracoes] Criando evento de teste:', {
        // empresaId: empresa.id,
        // calendarioId: calendarioSelecionado,
        // titulo: evento.summary,
        // inicio: evento.start.dateTime,
        // fim: evento.end.dateTime,
        // timestamp: new Date().toISOString()
      // });

      const resultado = await calendarService.criarEvento(calendarioSelecionado, evento);

      if (!resultado.sucesso) {
        throw new Error(resultado.mensagem);
      }

      setSucesso('Evento de teste criado com sucesso! Verifique seu Google Calendar.');
      // console.log('[Configuracoes] Evento de teste criado:', {
        // empresaId: empresa.id,
        // eventId: resultado.dados?.id,
        // htmlLink: resultado.dados?.htmlLink,
        // timestamp: new Date().toISOString()
      // });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      console.error('[Configuracoes] Erro ao criar evento de teste:', {
        error,
        errorMessage,
        timestamp: new Date().toISOString(),
        empresa: authService.getEmpresa()?.id
      });

      setErro(`Erro ao criar evento de teste: ${errorMessage}`);
      
      if (errorMessage.includes('ACESSO NEGADO')) {
        setTimeout(() => navigate('/login'), 2000);
      }
    } finally {
      setCriandoEventoTeste(false);
    }
  }, [calendarioSelecionado, eventoTeste, navigate]);

  // Excluir evento do Calendar
  const excluirEvento = useCallback(async () => {
    try {
      setExcluindoEvento(true);
      setErro(null);

      // VALIDAÇÃO CRÍTICA: Verificar autenticação
      const token = sessionStorage.getItem('token');
      if (!token) {
        console.error('[SECURITY-CHECK] Token perdido durante exclusão de evento');
        navigate('/login');
        return;
      }

      const empresa = authService.getEmpresa();
      if (!empresa) {
        throw new Error('ACESSO NEGADO: Empresa não identificada para exclusão de evento');
      }

      // Validar dados do evento
      if (!eventoParaExcluir.eventId.trim()) {
        throw new Error('Digite o ID do evento que deseja excluir');
      }

      const calendarioId = eventoParaExcluir.calendarioId || calendarioSelecionado;
      if (!calendarioId) {
        throw new Error('Selecione um calendário ou digite o ID do calendário');
      }

      if (!confirm(`Tem certeza que deseja excluir o evento "${eventoParaExcluir.eventId}"? Esta ação não pode ser desfeita.`)) {
        return;
      }

      // console.log('[Configuracoes] Excluindo evento:', {
        // empresaId: empresa.id,
        // calendarioId: calendarioId,
        // eventId: eventoParaExcluir.eventId,
        // timestamp: new Date().toISOString()
      // });

      const resultado = await calendarService.excluirEvento(calendarioId, eventoParaExcluir.eventId);

      // console.log('[Configuracoes] Resultado da exclusão:', {
        // sucesso: resultado.sucesso,
        // mensagem: resultado.mensagem,
        // eventId: eventoParaExcluir.eventId,
        // timestamp: new Date().toISOString()
      // });

      if (!resultado.sucesso) {
        throw new Error(resultado.mensagem);
      }

      setSucesso('Evento excluído com sucesso do Google Calendar!');
      
      // Limpar o campo do ID do evento
      setEventoParaExcluir(prev => ({
        ...prev,
        eventId: ''
      }));

      // Recarregar a lista de eventos
      if (mostrarEventos) {
        // Trigger reload sem depender da função carregarEventos
        setMostrarEventos(false);
        setTimeout(() => setMostrarEventos(true), 100);
      }

      // console.log('[Configuracoes] Evento excluído com sucesso:', {
        // empresaId: empresa.id,
        // eventId: eventoParaExcluir.eventId,
        // calendarioId: calendarioId,
        // timestamp: new Date().toISOString()
      // });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      console.error('[Configuracoes] Erro ao excluir evento:', {
        error,
        errorMessage,
        eventId: eventoParaExcluir.eventId,
        calendarioId: eventoParaExcluir.calendarioId || calendarioSelecionado,
        timestamp: new Date().toISOString(),
        empresa: authService.getEmpresa()?.id
      });

      setErro(`Erro ao excluir evento: ${errorMessage}`);
      
      if (errorMessage.includes('ACESSO NEGADO')) {
        setTimeout(() => navigate('/login'), 2000);
      }
    } finally {
      setExcluindoEvento(false);
    }
  }, [eventoParaExcluir, calendarioSelecionado, navigate, mostrarEventos]);

  // Editar evento do Calendar
  const editarEvento = useCallback(async () => {
    try {
      setEditandoEvento(true);
      setErro(null);
      setSucesso(null);

      // Validações
      if (!eventoParaEditar.eventId) {
        throw new Error('ID do evento é obrigatório');
      }

      if (!eventoParaEditar.titulo.trim()) {
        throw new Error('Título do evento é obrigatório');
      }

      if (!calendarioSelecionado) {
        throw new Error('Selecione um calendário primeiro');
      }

      // Preparar datas
      const dataInicioCompleta = `${eventoParaEditar.dataInicio}T${eventoParaEditar.horaInicio}:00`;
      const dataFimCompleta = `${eventoParaEditar.dataFim}T${eventoParaEditar.horaFim}:00`;

      const dataInicio = new Date(dataInicioCompleta);
      const dataFim = new Date(dataFimCompleta);

      // Validar datas
      if (isNaN(dataInicio.getTime()) || isNaN(dataFim.getTime())) {
        throw new Error('Datas inválidas');
      }

      if (dataFim <= dataInicio) {
        throw new Error('Data/hora de fim deve ser posterior ao início');
      }

      // console.log('[Configuracoes] Editando evento:', {
        // eventId: eventoParaEditar.eventId,
        // calendarioId: calendarioSelecionado,
        // titulo: eventoParaEditar.titulo,
        // dataInicio: dataInicio.toISOString(),
        // dataFim: dataFim.toISOString(),
        // timestamp: new Date().toISOString()
      // });

      // Preparar evento atualizado
      const eventoAtualizado = {
        summary: eventoParaEditar.titulo,
        description: eventoParaEditar.descricao || 'Evento editado via configurações',
        start: {
          dateTime: dataInicio.toISOString(),
          timeZone: 'America/Sao_Paulo'
        },
        end: {
          dateTime: dataFim.toISOString(),
          timeZone: 'America/Sao_Paulo'
        },
        status: 'confirmed' as const,
        visibility: 'private' as const
      };

      const resultado = await calendarService.atualizarEvento(calendarioSelecionado, eventoParaEditar.eventId, eventoAtualizado);
      
      if (resultado.sucesso) {
        setSucesso(`✅ Evento "${eventoParaEditar.titulo}" atualizado com sucesso no Google Calendar!`);
        
        // Limpar formulário
        setEventoParaEditar({
          eventId: '',
          titulo: '',
          descricao: '',
          dataInicio: '',
          horaInicio: '',
          dataFim: '',
          horaFim: ''
        });
        
        // Recarregar eventos se estiver mostrando
        if (mostrarEventos) {
          setTimeout(() => {
            carregarEventos();
          }, 1000);
        }
      } else {
        throw new Error(resultado.mensagem);
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      console.error('[Configuracoes] Erro ao editar evento:', {
        error,
        errorMessage,
        eventId: eventoParaEditar.eventId,
        calendarioId: calendarioSelecionado,
        timestamp: new Date().toISOString()
      });

      setErro(`Erro ao editar evento: ${errorMessage}`);
    } finally {
      setEditandoEvento(false);
    }
  }, [eventoParaEditar, calendarioSelecionado, mostrarEventos]);

  // Verificar conexão com Google Calendar
  const verificarConexaoCalendar = useCallback(async () => {
    try {
      setSalvando(true);

      const resultado = await calendarService.verificarConexao();
      
      if (resultado.sucesso) {
        alert('✅ Conexão com Google Calendar está funcionando perfeitamente!');
        // Carregar calendários automaticamente se a conexão estiver funcionando
        await carregarCalendarios();
      } else {
        // Verificar se é erro de token expirado
        if (resultado.erro === 'TOKEN_EXPIRED') {
          alert(`⚠️ ${resultado.mensagem}\n\n🔄 Solução: Use o botão "Reconectar com todos os escopos" na seção Gmail acima para renovar a autorização.`);
        } else {
          alert(`❌ Erro na conexão com Calendar: ${resultado.mensagem}`);
        }
      }
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      console.error('[Configuracoes] Erro no teste de conexão Calendar:', error);
      alert(`❌ Erro ao testar conexão com Google Calendar: ${errorMessage}`);
    } finally {
      setSalvando(false);
    }
  }, [carregarCalendarios]);

  // Carregar eventos do calendário selecionado
  const carregarEventos = useCallback(async () => {
    if (!calendarioSelecionado) {
      setErro('Selecione um calendário primeiro');
      return;
    }

    try {
      setCarregandoEventos(true);
      setErro(null);

      // Preparar datas para a busca
      let timeMin: string | undefined;
      let timeMax: string | undefined;

      if (filtroEventos.dataInicio) {
        timeMin = new Date(filtroEventos.dataInicio + 'T00:00:00').toISOString();
      } else {
        // Padrão: últimos 30 dias
        timeMin = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      }

      if (filtroEventos.dataFim) {
        timeMax = new Date(filtroEventos.dataFim + 'T23:59:59').toISOString();
      } else {
        // Padrão: próximos 60 dias
        timeMax = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString();
      }

      // console.log('[Configuracoes] Carregando eventos do calendário:', {
        // calendarioId: calendarioSelecionado,
        // timeMin: new Date(timeMin).toLocaleDateString('pt-BR'),
        // timeMax: new Date(timeMax).toLocaleDateString('pt-BR'),
        // timestamp: new Date().toISOString()
      // });

      const resultado = await calendarService.listarEventos(calendarioSelecionado, timeMin, timeMax);
      
      if (resultado.sucesso && resultado.dados) {
        setEventos(resultado.dados);
        setMostrarEventos(true);
        
        // console.log('[Configuracoes] Eventos carregados:', {
          // quantidade: resultado.dados.length,
          // calendarioId: calendarioSelecionado,
          // periodo: `${new Date(timeMin).toLocaleDateString('pt-BR')} até ${new Date(timeMax).toLocaleDateString('pt-BR')}`
        // });

        if (resultado.dados.length === 0) {
          setSucesso(`Nenhum evento encontrado no período de ${new Date(timeMin).toLocaleDateString('pt-BR')} até ${new Date(timeMax).toLocaleDateString('pt-BR')}`);
        } else {
          setSucesso(`${resultado.dados.length} eventos encontrados!`);
        }
      } else {
        throw new Error(resultado.mensagem || 'Erro ao carregar eventos');
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      console.error('[Configuracoes] Erro ao carregar eventos:', {
        error,
        errorMessage,
        timestamp: new Date().toISOString()
      });

      setErro(`Erro ao carregar eventos: ${errorMessage}`);
    } finally {
      setCarregandoEventos(false);
    }
  }, [calendarioSelecionado, filtroEventos.dataInicio, filtroEventos.dataFim]);

  // Carregar calendários quando conta Google estiver conectada
  useEffect(() => {
    if (contaGoogleConectada && integracaoGoogle.agendaHabilitada && calendarios.length === 0) {
      carregarCalendarios();
    }
  }, [contaGoogleConectada, integracaoGoogle.agendaHabilitada, calendarios.length, carregarCalendarios]);

  // Carregar disponibilidades quando usuário estiver disponível
  useEffect(() => {
    if (usuarioId) {
      carregarDisponibilidades();
    }
  }, [usuarioId, carregarDisponibilidades]);

  // Carregar preferências de alertas quando a aba for selecionada
  useEffect(() => {
    if (abaSelecionada === 'alertas') {
      carregarPreferenciasAlertas();
    }
  }, [abaSelecionada, carregarPreferenciasAlertas]);

  // Pré-preencher campos de notificação com dados da empresa
  useEffect(() => {
    if (dadosEmpresa && dadosEmpresa.id) {
      setTemplatesNotificacao(prev => ({
        ...prev,
        // Só preenche se estiver vazio (não sobrescreve configurações existentes)
        nomeExibicao: prev.nomeExibicao || dadosEmpresa.nome_fantasia || dadosEmpresa.nome || '',
        telefoneContato: prev.telefoneContato || dadosEmpresa.telefone || '',
        enderecoConsultorio: prev.enderecoConsultorio || dadosEmpresa.endereco || ''
      }));
    }
  }, [dadosEmpresa]);

  // ==================== FIM FUNÇÕES GOOGLE CALENDAR ====================

  if (carregando) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3"></div>
          <p>Carregando configurações...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
                  <div className="col-md-12 mb-4">
              <h1 className="m-0">
                <i className="fas fa-cogs mr-2"></i>
                Configurações do Sistema
              </h1>
            </div>
            <div className="col-sm-6">
              <div className="float-sm-right">
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={salvarConfiguracoes}
                  disabled={salvando}
                >
                  {salvando ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2"></span>
                      Salvando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save mr-2"></i>
                      Salvar Configurações
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Alertas */}
      {erro && (
        <div className="alert alert-danger alert-dismissible">
          <button type="button" className="close" onClick={() => setErro(null)}>
            <span>&times;</span>
          </button>
          <i className="icon fas fa-exclamation-triangle"></i>
          {erro}
        </div>
      )}

      {sucesso && (
        <div className="alert alert-success alert-dismissible">
          <button type="button" className="close" onClick={() => setSucesso(null)}>
            <span>&times;</span>
          </button>
          <i className="icon fas fa-check"></i>
          {sucesso}
        </div>
      )}

      {/* Navegação por abas */}
      <div className="card card-primary card-outline card-tabs">
        <div className="card-header p-0 pt-1 border-bottom-0">
          <ul className="nav nav-tabs" role="tablist">
            <li className="nav-item">
              <a
                className={`nav-link ${abaSelecionada === 'empresa' ? 'active' : ''}`}
                onClick={() => setAbaSelecionada('empresa')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setAbaSelecionada('empresa');
                  }
                }}
                role="tab"
                style={{ cursor: 'pointer' }}
                tabIndex={0}
              >
                <i className="fas fa-building mr-2"></i>
                Dados da Empresa
              </a>
            </li>
            <li className="nav-item">
              <a
                className={`nav-link ${abaSelecionada === 'horarios' ? 'active' : ''}`}
                onClick={() => setAbaSelecionada('horarios')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setAbaSelecionada('horarios');
                  }
                }}
                role="tab"
                style={{ cursor: 'pointer' }}
                tabIndex={0}
              >
                <i className="fas fa-clock mr-2"></i>
                Horários de Trabalho
              </a>
            </li>
            <li className="nav-item">
              <a
                className={`nav-link ${abaSelecionada === 'tema' ? 'active' : ''}`}
                onClick={() => setAbaSelecionada('tema')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setAbaSelecionada('tema');
                  }
                }}
                role="tab"
                style={{ cursor: 'pointer' }}
                tabIndex={0}
              >
                <i className="fas fa-palette mr-2"></i>
                Tema e Interface
              </a>
            </li>
            <li className="nav-item">
              <a
                className={`nav-link ${abaSelecionada === 'google' ? 'active' : ''}`}
                onClick={() => setAbaSelecionada('google')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setAbaSelecionada('google');
                  }
                }}
                role="tab"
                style={{ cursor: 'pointer' }}
                tabIndex={0}
              >
                <i className="fab fa-google mr-2"></i>
                Integração Google
              </a>
            </li>
            <li className="nav-item">
              <a
                className={`nav-link ${abaSelecionada === 'notificacoes' ? 'active' : ''}`}
                onClick={() => setAbaSelecionada('notificacoes')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setAbaSelecionada('notificacoes');
                  }
                }}
                role="tab"
                style={{ cursor: 'pointer' }}
                tabIndex={0}
              >
                <i className="fas fa-envelope mr-2"></i>
                Personalizar Notificações por Email
              </a>
            </li>
            <li className="nav-item">
              <a
                className={`nav-link ${abaSelecionada === 'alertas' ? 'active' : ''}`}
                onClick={() => setAbaSelecionada('alertas')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setAbaSelecionada('alertas');
                  }
                }}
                role="tab"
                style={{ cursor: 'pointer' }}
                tabIndex={0}
              >
                <i className="fas fa-bell mr-2"></i>
                Alertas do Sistema
              </a>
            </li>
          </ul>
        </div>
        <div className="card-body">
          <div className="tab-content">
            {/* Aba: Dados da Empresa */}
            {abaSelecionada === 'empresa' && (
              <div className="tab-pane active">
                <div className="row">
                  <div className="col-md-8">
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">
                          <i className="fas fa-info-circle mr-2"></i>
                          Informações Básicas
                        </h3>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group">
                              <label htmlFor="nomeEmpresa">Nome da Empresa *</label>
                              <input
                                type="text"
                                className="form-control"
                                id="nomeEmpresa"
                                value={dadosEmpresa.nome}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                  handleEmpresaChange('nome', e.target.value)
                                }
                                required
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group">
                              <label htmlFor="nomeFantasia">Nome Fantasia</label>
                              <input
                                type="text"
                                className="form-control"
                                id="nomeFantasia"
                                value={dadosEmpresa.nome_fantasia ?? ''}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                  handleEmpresaChange('nome_fantasia', e.target.value)
                                }
                              />
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group">
                              <label htmlFor="cnpj">CNPJ</label>
                              <input
                                type="text"
                                className="form-control"
                                id="cnpj"
                                value={dadosEmpresa.cnpj ?? ''}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                  handleEmpresaChange('cnpj', e.target.value)
                                }
                                placeholder="00.000.000/0000-00"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group">
                              <label htmlFor="emailEmpresa">E-mail *</label>
                              <input
                                type="email"
                                className="form-control"
                                id="emailEmpresa"
                                value={dadosEmpresa.email}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                  handleEmpresaChange('email', e.target.value)
                                }
                                required
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group">
                              <label htmlFor="telefoneEmpresa">Telefone *</label>
                              <input
                                type="tel"
                                className="form-control"
                                id="telefoneEmpresa"
                                value={dadosEmpresa.telefone}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                  handleEmpresaChange('telefone', e.target.value)
                                }
                                required
                              />
                            </div>
                          </div>
                        </div>

                        <div className="form-group">
                          <label htmlFor="endereco">Endereço Completo</label>
                          <input
                            type="text"
                            className="form-control"
                            id="endereco"
                            value={dadosEmpresa.endereco ?? ''}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => 
                              handleEmpresaChange('endereco', e.target.value)
                            }
                            placeholder="Rua, número, bairro"
                          />
                        </div>

                        <div className="row">
                          <div className="col-md-4">
                            <div className="form-group">
                              <label htmlFor="cep">CEP</label>
                              <input
                                type="text"
                                className="form-control"
                                id="cep"
                                value={dadosEmpresa.cep ?? ''}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                  handleEmpresaChange('cep', e.target.value)
                                }
                                placeholder="00000-000"
                              />
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="form-group">
                              <label htmlFor="cidade">Cidade</label>
                              <input
                                type="text"
                                className="form-control"
                                id="cidade"
                                value={dadosEmpresa.cidade ?? ''}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                  handleEmpresaChange('cidade', e.target.value)
                                }
                              />
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="form-group">
                              <label htmlFor="estado">Estado</label>
                              <select
                                className="form-control"
                                id="estado"
                                value={dadosEmpresa.estado ?? ''}
                                onChange={(e: ChangeEvent<HTMLSelectElement>) => 
                                  handleEmpresaChange('estado', e.target.value)
                                }
                              >
                                <option value="">Selecione...</option>
                                <option value="AC">Acre</option>
                                <option value="AL">Alagoas</option>
                                <option value="AP">Amapá</option>
                                <option value="AM">Amazonas</option>
                                <option value="BA">Bahia</option>
                                <option value="CE">Ceará</option>
                                <option value="DF">Distrito Federal</option>
                                <option value="ES">Espírito Santo</option>
                                <option value="GO">Goiás</option>
                                <option value="MA">Maranhão</option>
                                <option value="MT">Mato Grosso</option>
                                <option value="MS">Mato Grosso do Sul</option>
                                <option value="MG">Minas Gerais</option>
                                <option value="PA">Pará</option>
                                <option value="PB">Paraíba</option>
                                <option value="PR">Paraná</option>
                                <option value="PE">Pernambuco</option>
                                <option value="PI">Piauí</option>
                                <option value="RJ">Rio de Janeiro</option>
                                <option value="RN">Rio Grande do Norte</option>
                                <option value="RS">Rio Grande do Sul</option>
                                <option value="RO">Rondônia</option>
                                <option value="RR">Roraima</option>
                                <option value="SC">Santa Catarina</option>
                                <option value="SP">São Paulo</option>
                                <option value="SE">Sergipe</option>
                                <option value="TO">Tocantins</option>
                              </select>
                            </div>
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">
                          <i className="fas fa-image mr-2"></i>
                          Logo da Empresa
                        </h3>
                      </div>
                      <div className="card-body text-center">
                        <div className="mb-3">
                          {logoUrl ? (
                            <img 
                              src={logoUrl} 
                              alt="Logo da empresa" 
                              className="img-fluid rounded mb-3"
                              style={{ maxHeight: '150px', objectFit: 'contain' }}
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = '/img/logo-placeholder.png';
                              }}
                            />
                          ) : (
                            <div className="p-4 bg-light border rounded">
                              <i className="fas fa-building fa-3x text-muted"></i>
                              <p className="text-muted mt-2 mb-0">Nenhuma logo cadastrada</p>
                            </div>
                          )}
                        </div>
                        
                        <input
                          type="file"
                          id="logoUpload"
                          className="d-none"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          disabled={uploadandoLogo}
                          onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              if (file.size > 2 * 1024 * 1024) {
                                setErro('O arquivo é muito grande. Máximo: 2MB');
                                return;
                              }
                              setUploadandoLogo(true);
                              setErro(null);
                              try {
                                const resultado = await empresasService.uploadLogo(file);
                                if (resultado.sucesso) {
                                  setSucesso('Logo atualizada com sucesso!');
                                  // Recarregar URL da logo
                                  await carregarLogoEmpresa();
                                  setTimeout(() => setSucesso(null), 3000);
                                } else {
                                  setErro(resultado.mensagem || 'Erro ao enviar logo');
                                }
                              } catch (error) {
                                console.error('Erro no upload:', error);
                                setErro('Erro ao enviar logo');
                              } finally {
                                setUploadandoLogo(false);
                                e.target.value = ''; // Reset input
                              }
                            }
                          }}
                        />
                        <label htmlFor="logoUpload" className="btn btn-outline-primary mb-0">
                          {uploadandoLogo ? (
                            <>
                              <span className="spinner-border spinner-border-sm mr-2"></span>
                              Enviando...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-upload mr-2"></i>
                              {logoUrl ? 'Alterar Logo' : 'Enviar Logo'}
                            </>
                          )}
                        </label>
                        <small className="form-text text-muted d-block mt-2">
                          Formatos: JPG, PNG, GIF, WebP. Máximo: 2MB
                        </small>
                      </div>
                    </div>

                    {/* Card de Informações do Plano Contratado */}
                    <div className="card card-primary card-outline">
                      <div className="card-header">
                        <h3 className="card-title">
                          <i className="fas fa-crown mr-2"></i>
                          Plano Contratado
                        </h3>
                      </div>
                      <div className="card-body">
                        {dadosEmpresa.plano_nome ? (
                          <>
                            <div className="info-box bg-gradient-primary">
                              <span className="info-box-icon">
                                <i className="fas fa-award"></i>
                              </span>
                              <div className="info-box-content">
                                <span className="info-box-text">Plano Atual</span>
                                <span className="info-box-number">{dadosEmpresa.plano_nome}</span>
                                <span className="badge badge-light mt-1">
                                  R$ {dadosEmpresa.plano_valor_mensal ? Number(dadosEmpresa.plano_valor_mensal).toFixed(2) : '0,00'}/mês
                                </span>
                              </div>
                            </div>

                            <div className="row mt-3">
                              <div className="col-12">
                                <p className="mb-2">
                                  <strong><i className="fas fa-check-circle text-success mr-1"></i> Status:</strong>
                                  <span className={`badge ml-2 ${
                                    dadosEmpresa.status_assinatura === 'ativo' 
                                      ? 'badge-success' 
                                      : dadosEmpresa.status_assinatura === 'pendente'
                                      ? 'badge-warning'
                                      : 'badge-danger'
                                  }`}>
                                    {dadosEmpresa.status_assinatura?.toUpperCase() || 'INDEFINIDO'}
                                  </span>
                                </p>
                                
                                {dadosEmpresa.data_expiracao_plano && (
                                  <p className="mb-2">
                                    <strong><i className="fas fa-calendar-alt text-info mr-1"></i> Validade:</strong>
                                    <span className="ml-2">
                                      {new Date(dadosEmpresa.data_expiracao_plano).toLocaleDateString('pt-BR')}
                                    </span>
                                  </p>
                                )}

                                <hr className="my-2" />

                                <p className="mb-1 text-sm">
                                  <strong>Limites do Plano:</strong>
                                </p>
                                <ul className="list-unstyled ml-3 text-sm">
                                  <li>
                                    <i className="fas fa-users text-primary mr-2"></i>
                                    Pacientes: <strong>{dadosEmpresa.plano_limite_pacientes || '∞'}</strong>
                                  </li>
                                  <li>
                                    <i className="fas fa-user-friends text-info mr-2"></i>
                                    Usuários: <strong>{dadosEmpresa.plano_limite_usuarios || '∞'}</strong>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="alert alert-warning mb-0">
                            <i className="fas fa-exclamation-triangle mr-2"></i>
                            Nenhum plano contratado
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Card de Uso Atual (Quotas) */}
                    <div className="card card-info card-outline">
                      <div className="card-header">
                        <h3 className="card-title">
                          <i className="fas fa-chart-bar mr-2"></i>
                          Uso Atual
                        </h3>
                      </div>
                      <div className="card-body">
                        {dadosEmpresa.quotas_utilizadas ? (
                          <>
                            {/* Pacientes */}
                            <div className="mb-3">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="text-sm">
                                  <i className="fas fa-users text-primary mr-1"></i>
                                  Pacientes Ativos
                                </span>
                                <span className="text-sm font-weight-bold">
                                  {dadosEmpresa.quotas_utilizadas.pacientes_criados ?? 0} / {dadosEmpresa.limites?.max_pacientes || '∞'}
                                </span>
                              </div>
                              <div className="progress" style={{ height: '6px' }}>
                                <div 
                                  className={`progress-bar ${
                                    ((dadosEmpresa.quotas_utilizadas.pacientes_criados ?? 0) / (dadosEmpresa.limites?.max_pacientes || 100)) > 0.8 
                                      ? 'bg-danger' 
                                      : 'bg-primary'
                                  }`}
                                  style={{ 
                                    width: `${Math.min(
                                      ((dadosEmpresa.quotas_utilizadas.pacientes_criados ?? 0) / (dadosEmpresa.limites?.max_pacientes || 100)) * 100, 
                                      100
                                    )}%` 
                                  }}
                                ></div>
                              </div>
                            </div>

                            {/* Usuários */}
                            <div className="mb-3">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="text-sm">
                                  <i className="fas fa-user-friends text-info mr-1"></i>
                                  Usuários Ativos
                                </span>
                                <span className="text-sm font-weight-bold">
                                  {dadosEmpresa.quotas_utilizadas.usuarios_criados ?? 0} / {dadosEmpresa.limites?.max_usuarios || '∞'}
                                </span>
                              </div>
                              <div className="progress" style={{ height: '6px' }}>
                                <div 
                                  className={`progress-bar ${
                                    ((dadosEmpresa.quotas_utilizadas.usuarios_criados ?? 0) / (dadosEmpresa.limites?.max_usuarios || 100)) > 0.8 
                                      ? 'bg-danger' 
                                      : 'bg-info'
                                  }`}
                                  style={{ 
                                    width: `${Math.min(
                                      ((dadosEmpresa.quotas_utilizadas.usuarios_criados ?? 0) / (dadosEmpresa.limites?.max_usuarios || 100)) * 100, 
                                      100
                                    )}%` 
                                  }}
                                ></div>
                              </div>
                            </div>

                            {/* Psicólogos */}
                            {dadosEmpresa.quotas_utilizadas.psicologos_criados !== undefined && (
                              <div className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                  <span className="text-sm">
                                    <i className="fas fa-user-md text-success mr-1"></i>
                                    Psicólogos
                                  </span>
                                  <span className="text-sm font-weight-bold">
                                    {dadosEmpresa.quotas_utilizadas.psicologos_criados ?? 0} / {dadosEmpresa.limites?.max_psicologos || '∞'}
                                  </span>
                                </div>
                                <div className="progress" style={{ height: '6px' }}>
                                  <div 
                                    className="progress-bar bg-success"
                                    style={{ 
                                      width: `${Math.min(
                                        ((dadosEmpresa.quotas_utilizadas.psicologos_criados ?? 0) / (dadosEmpresa.limites?.max_psicologos || 100)) * 100, 
                                        100
                                      )}%` 
                                    }}
                                  ></div>
                                </div>
                              </div>
                            )}

                            {/* Atendentes */}
                            {dadosEmpresa.quotas_utilizadas.atendentes_criados !== undefined && (
                              <div className="mb-2">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                  <span className="text-sm">
                                    <i className="fas fa-headset text-warning mr-1"></i>
                                    Atendentes
                                  </span>
                                  <span className="text-sm font-weight-bold">
                                    {dadosEmpresa.quotas_utilizadas.atendentes_criados ?? 0} / {dadosEmpresa.limites?.max_atendentes || '∞'}
                                  </span>
                                </div>
                                <div className="progress" style={{ height: '6px' }}>
                                  <div 
                                    className="progress-bar bg-warning"
                                    style={{ 
                                      width: `${Math.min(
                                        ((dadosEmpresa.quotas_utilizadas.atendentes_criados ?? 0) / (dadosEmpresa.limites?.max_atendentes || 100)) * 100, 
                                        100
                                      )}%` 
                                    }}
                                  ></div>
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="alert alert-info mb-0">
                            <i className="fas fa-info-circle mr-2"></i>
                            Nenhuma informação de uso disponível
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Aba: Horários de Trabalho */}
            {abaSelecionada === 'horarios' && (
              <div className="tab-pane active">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">
                      <i className="fas fa-calendar-week mr-2"></i>
                      Configuração de Horários de Trabalho
                    </h3>
                  </div>
                  <div className="card-body">
                    {carregandoDisponibilidades ? (
                      <div className="text-center py-4">
                        <i className="fas fa-spinner fa-spin fa-2x text-primary"></i>
                        <p className="mt-2 text-muted">Carregando horários de trabalho...</p>
                      </div>
                    ) : (
                      <>
                        <div className="alert alert-warning">
                          <i className="fas fa-exclamation-triangle mr-2"></i>
                          <strong>Importante:</strong> Configure os horários de funcionamento para cada dia da semana. 
                          As mudanças ficam apenas na interface até você clicar em "Salvar Configurações" no topo da página.
                          Os intervalos configurados bloquearão automaticamente esses períodos na agenda.
                        </div>

                    {horariosTrabalho.map((horario, index) => (
                      <div key={horario.id} className="card card-outline card-secondary mb-3">
                        <div className="card-header">
                          <div className="row align-items-center">
                            <div className="col-md-3">
                              <div className="custom-control custom-switch">
                                <input
                                  type="checkbox"
                                  className="custom-control-input"
                                  id={`ativo-${index}`}
                                  checked={horario.ativo}
                                  onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                    handleHorarioChange(index, 'ativo', e.target.checked)
                                  }
                                />
                                <label className="custom-control-label" htmlFor={`ativo-${index}`}>
                                  <strong>
                                    {['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'][horario.diaSemana]}
                                  </strong>
                                </label>
                              </div>
                            </div>
                            {horario.ativo && (
                              <>
                                <div className="col-md-4">
                                  <label className="form-label text-sm" htmlFor={`horarioInicio-${index}`}>Início</label>
                                  <input
                                    id={`horarioInicio-${index}`}
                                    type="time"
                                    className="form-control form-control-sm"
                                    value={horario.horaInicio}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                      handleHorarioChange(index, 'horaInicio', e.target.value)
                                    }
                                  />
                                </div>
                                <div className="col-md-4">
                                  <label className="form-label text-sm" htmlFor={`horarioFim-${index}`}>Fim</label>
                                  <input
                                    id={`horarioFim-${index}`}
                                    type="time"
                                    className="form-control form-control-sm"
                                    value={horario.horaFim}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                      handleHorarioChange(index, 'horaFim', e.target.value)
                                    }
                                  />
                                </div>
                                <div className="col-md-1">
                                  <div className="form-label text-sm">&nbsp;</div>
                                  <div>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => adicionarIntervalo(index)}
                                    >
                                      <i className="fas fa-plus mr-1"></i>
                                    </button>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        {horario.ativo && (
                          <div className="card-body pt-2">
                            <div className="row">
                              <div className="col-md-12">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => adicionarIntervalo(index)}
                                >
                                  <i className="fas fa-plus mr-1"></i>
                                  Adicionar Intervalo
                                </button>
                                <small className="text-muted ml-2">
                                  <i className="fas fa-info-circle mr-1"></i>
                                  Os intervalos configurados bloquearão automaticamente esses horários na agenda
                                </small>
                              </div>
                            </div>
                          </div>
                        )}

                        {horario.ativo && horario.intervalos.length > 0 && (
                          <div className="card-body pt-2">
                            <h6 className="text-muted mb-3">
                              <i className="fas fa-pause mr-1"></i>
                              Intervalos
                            </h6>
                            
                            {/* Header com labels */}
                            <div className="row mb-2">
                              <div className="col-md-4">
                                <small className="text-muted"><strong>Início</strong></small>
                              </div>
                              <div className="col-md-4">
                                <small className="text-muted"><strong>Fim</strong></small>
                              </div>
                              <div className="col-md-3">
                                <small className="text-muted"><strong>Descrição</strong></small>
                              </div>
                              <div className="col-md-1">
                                <small className="text-muted"><strong>Ação</strong></small>
                              </div>
                            </div>
                            
                            {horario.intervalos.map((intervalo) => (
                              <div key={intervalo.id} className={`row mb-2 ${intervalo.temporario || intervalo.id.toString().startsWith('temp_') ? 'border border-warning rounded p-2' : ''}`}>
                                {(intervalo.temporario || intervalo.id.toString().startsWith('temp_')) && (
                                  <div className="col-12 mb-2">
                                    <small className="badge badge-warning">
                                      <i className="fas fa-clock mr-1"></i>
                                      Não salvo - Use "Salvar Configurações" para persistir
                                    </small>
                                  </div>
                                )}
                                <div className="col-md-4">
                                  <input
                                    type="time"
                                    className="form-control form-control-sm"
                                    value={intervalo.horaInicio}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                      const novosIntervalos = horario.intervalos.map(i => 
                                        i.id === intervalo.id 
                                          ? { ...i, horaInicio: e.target.value }
                                          : i
                                      );
                                      handleHorarioChange(index, 'intervalos', novosIntervalos);
                                    }}
                                  />
                                </div>
                                <div className="col-md-4">
                                  <input
                                    type="time"
                                    className="form-control form-control-sm"
                                    value={intervalo.horaFim}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                      const novosIntervalos = horario.intervalos.map(i => 
                                        i.id === intervalo.id 
                                          ? { ...i, horaFim: e.target.value }
                                          : i
                                      );
                                      handleHorarioChange(index, 'intervalos', novosIntervalos);
                                    }}
                                  />
                                </div>
                                <div className="col-md-3">
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Ex: Almoço, Pausa"
                                    value={intervalo.descricao}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                      const novosIntervalos = horario.intervalos.map(i => 
                                        i.id === intervalo.id 
                                          ? { ...i, descricao: e.target.value }
                                          : i
                                      );
                                      handleHorarioChange(index, 'intervalos', novosIntervalos);
                                    }}
                                  />
                                </div>
                                <div className="col-md-1">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => removerIntervalo(index, intervalo.id)}
                                    title="Excluir intervalo"
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Aba: Tema e Interface */}
            {abaSelecionada === 'tema' && (
              <div className="tab-pane active">
                <ThemeConfiguration />
              </div>
            )}

            {/* Aba: Integração Google */}
            {abaSelecionada === 'google' && (
              <div className="tab-pane active">
                <div className="alert alert-info">
                  <i className="fas fa-info-circle mr-2"></i>
                  <strong>Integração OAuth2:</strong> 
                  Conecte sua conta Google para enviar emails diretamente do Gmail. 
                  Seus dados ficam seguros e você pode desconectar a qualquer momento.
                </div>

                {contaGoogleConectada && dadosContaGoogle ? (
                  // Conta conectada
                  <div className="row">
                    <div className="col-md-12">
                      <div className="card card-success">
                        <div className="card-header">
                          <h3 className="card-title">
                            <i className="fas fa-check-circle mr-2"></i>
                            Conta Google Conectada
                          </h3>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-8">
                              <table className="table table-borderless">
                                <thead className="sr-only">
                                  <tr>
                                    <th>Propriedade</th>
                                    <th>Valor</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <td><strong>E-mail:</strong></td>
                                    <td>{dadosContaGoogle.email}</td>
                                  </tr>
                                  <tr>
                                    <td><strong>Nome:</strong></td>
                                    <td>{dadosContaGoogle.nome}</td>
                                  </tr>
                                  <tr>
                                    <td><strong>Conectado em:</strong></td>
                                    <td>{new Date(dadosContaGoogle.conectadoEm).toLocaleString('pt-BR')}</td>
                                  </tr>
                                  <tr>
                                    <td><strong>Status:</strong></td>
                                    <td>
                                      <span className="badge badge-success">
                                        <i className="fas fa-check mr-1"></i>
                                        Ativo e funcional
                                      </span>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                            <div className="col-md-4 text-center">
                              <i className="fab fa-google fa-5x text-success mb-3"></i>
                              <br />
                              <div className="btn-group-vertical">
                                <button
                                  type="button"
                                  className="btn btn-info mb-2"
                                  onClick={testarConexaoGoogle}
                                  disabled={salvando}
                                >
                                  {salvando ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm mr-2"></span>
                                      Testando...
                                    </>
                                  ) : (
                                    <>
                                      <i className="fas fa-vial mr-2"></i>
                                      Testar Conexão
                                    </>
                                  )}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-info mr-2"
                                  onClick={reconectarComTodosEscopos}
                                  disabled={conectandoGoogle}
                                  title="Reconectar para ativar Google Calendar"
                                >
                                  {conectandoGoogle ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm mr-2"></span>
                                      Reconectando...
                                    </>
                                  ) : (
                                    <>
                                      <i className="fas fa-sync-alt mr-2"></i>
                                      Reconectar + Calendar
                                    </>
                                  )}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-warning"
                                  onClick={desconectarContaGoogle}
                                  disabled={conectandoGoogle}
                                >
                                  {conectandoGoogle ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm mr-2"></span>
                                      Desconectando...
                                    </>
                                  ) : (
                                    <>
                                      <i className="fas fa-unlink mr-2"></i>
                                      Desconectar
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>

                          <hr />

                          <div className="row">
                            <div className="col-md-6">
                              <h5>
                                <i className="fas fa-envelope mr-2"></i>
                                Configurações de E-mail
                              </h5>
                              
                              <div className="form-group">
                                <label htmlFor="nomeRemetente">Nome do Remetente</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  id="nomeRemetente"
                                  value={integracaoGoogle.nomeRemetente}
                                  onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                    handleGoogleChange('nomeRemetente', e.target.value)
                                  }
                                  placeholder="Nome que aparecerá nos e-mails"
                                />
                              </div>

                              <div className="form-group">
                                <label htmlFor="assinaturaEmail">Assinatura do E-mail</label>
                                <textarea
                                  className="form-control"
                                  id="assinaturaEmail"
                                  rows={4}
                                  value={integracaoGoogle.assinaturaEmail}
                                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) => 
                                    handleGoogleChange('assinaturaEmail', e.target.value)
                                  }
                                  placeholder="Assinatura que aparecerá no final dos e-mails"
                                />
                              </div>

                              <div className="mt-3">
                                <button
                                  type="button"
                                  className="btn btn-success"
                                  onClick={salvarAssinatura}
                                  disabled={salvando}
                                >
                                  {salvando ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm mr-2"></span>
                                      Salvando...
                                    </>
                                  ) : (
                                    <>
                                      <i className="fas fa-save mr-2"></i>
                                      Salvar Assinatura
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>

                            <div className="col-md-6">
                              <h5>
                                <i className="fas fa-paper-plane mr-2"></i>
                                Teste de Envio de E-mail
                              </h5>
                              
                              <div className="form-group">
                                <label htmlFor="emailDestinatario">E-mail de Teste</label>
                                <input
                                  type="email"
                                  className="form-control"
                                  id="emailDestinatario"
                                  value={emailTeste.destinatario}
                                  onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                    setEmailTeste(prev => ({ ...prev, destinatario: e.target.value }))
                                  }
                                  placeholder="Digite um e-mail para teste"
                                />
                              </div>

                              <div className="form-group">
                                <label htmlFor="assuntoTeste">Assunto</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  id="assuntoTeste"
                                  value={emailTeste.assunto}
                                  onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                    setEmailTeste(prev => ({ ...prev, assunto: e.target.value }))
                                  }
                                />
                              </div>

                              <div className="form-group">
                                <label htmlFor="mensagemTeste">Mensagem</label>
                                <textarea
                                  className="form-control"
                                  id="mensagemTeste"
                                  rows={4}
                                  value={emailTeste.mensagem}
                                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) => 
                                    setEmailTeste(prev => ({ ...prev, mensagem: e.target.value }))
                                  }
                                />
                              </div>

                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={enviarEmailTeste}
                                disabled={enviandoTeste || !emailTeste.destinatario}
                              >
                                {enviandoTeste ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm mr-2"></span>
                                    Enviando...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-envelope mr-2"></i>
                                    Enviar Teste
                                  </>
                                )}
                              </button>
                            </div>
                          </div>

                          <hr />

                          <div className="row">
                            <div className="col-md-12">
                              <h5>
                                <i className="fas fa-calendar mr-2"></i>
                                Google Calendar
                              </h5>

                              <div className="custom-control custom-switch mb-3">
                                <input
                                  type="checkbox"
                                  className="custom-control-input"
                                  id="agendaHabilitada"
                                  checked={integracaoGoogle.agendaHabilitada}
                                  onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                    handleGoogleChange('agendaHabilitada', e.target.checked)
                                  }
                                />
                                <label className="custom-control-label" htmlFor="agendaHabilitada">
                                  <strong>Sincronizar com Google Calendar</strong>
                                </label>
                              </div>

                              {calendarios.length === 0 && integracaoGoogle.agendaHabilitada && (
                                <div className="alert alert-warning">
                                  <i className="fas fa-exclamation-triangle mr-2"></i>
                                  <strong>Atenção:</strong> Para usar o Google Calendar, você precisa reconectar sua conta Google para autorizar as novas permissões.
                                  <br />
                                  <button 
                                    type="button" 
                                    className="btn btn-warning btn-sm mt-2"
                                    onClick={reconectarComTodosEscopos}
                                    disabled={conectandoGoogle}
                                  >
                                    <i className="fas fa-sync-alt mr-1"></i>
                                    Reconectar com Calendar
                                  </button>
                                </div>
                              )}

                              {integracaoGoogle.agendaHabilitada && (
                                <div className="card card-outline card-info mb-3">
                                  <div className="card-header">
                                    <h6 className="mb-0">
                                      <i className="fas fa-calendar-check mr-2"></i>
                                      Configurações do Calendar
                                    </h6>
                                  </div>
                                  <div className="card-body">
                                    <div className="row">
                                      <div className="col-md-6">
                                        <div className="form-group">
                                          <label htmlFor="calendarioSelect">Calendário Principal</label>
                                          <div className="input-group">
                                            <select
                                              className="form-control"
                                              id="calendarioSelect"
                                              value={calendarioSelecionado}
                                              onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                                                setCalendarioSelecionado(e.target.value);
                                                handleGoogleChange('agendaId', e.target.value);
                                              }}
                                            >
                                              <option value="">Selecione um calendário...</option>
                                              {calendarios.map((calendario) => (
                                                <option key={calendario.id} value={calendario.id}>
                                                  {calendario.summary} {calendario.primary ? '(Principal)' : ''}
                                                </option>
                                              ))}
                                            </select>
                                            <div className="input-group-append">
                                              <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={carregarCalendarios}
                                                disabled={carregandoCalendarios}
                                                title="Recarregar calendários"
                                              >
                                                {carregandoCalendarios ? (
                                                  <span className="spinner-border spinner-border-sm"></span>
                                                ) : (
                                                  <i className="fas fa-sync-alt"></i>
                                                )}
                                              </button>
                                            </div>
                                          </div>
                                          <small className="form-text text-muted">
                                            Selecione o calendário onde as consultas serão criadas automaticamente.
                                          </small>
                                        </div>

                                        <div className="form-group">
                                          <label htmlFor="fusoHorario">Fuso Horário</label>
                                          <select
                                            className="form-control"
                                            id="fusoHorario"
                                            value={integracaoGoogle.fusoHorario}
                                            onChange={(e: ChangeEvent<HTMLSelectElement>) => 
                                              handleGoogleChange('fusoHorario', e.target.value)
                                            }
                                          >
                                            <option value="America/Sao_Paulo">Brasília (UTC-3)</option>
                                            <option value="America/Manaus">Manaus (UTC-4)</option>
                                            <option value="America/Rio_Branco">Rio Branco (UTC-5)</option>
                                          </select>
                                        </div>

                                        <div className="mt-3">
                                          <button
                                            type="button"
                                            className="btn btn-info mr-2"
                                            onClick={verificarConexaoCalendar}
                                            disabled={salvando}
                                          >
                                            {salvando ? (
                                              <>
                                                <span className="spinner-border spinner-border-sm mr-2"></span>
                                                Testando...
                                              </>
                                            ) : (
                                              <>
                                                <i className="fas fa-calendar-check mr-2"></i>
                                                Testar Calendar
                                              </>
                                            )}
                                          </button>

                                          <button
                                            type="button"
                                            className="btn btn-warning mr-2"
                                            onClick={recarregarTokensCalendar}
                                            disabled={salvando}
                                            title="Use este botão se trocou de conta Google e o Calendar não atualizou"
                                          >
                                            {salvando ? (
                                              <>
                                                <span className="spinner-border spinner-border-sm mr-2"></span>
                                                Sincronizando...
                                              </>
                                            ) : (
                                              <>
                                                <i className="fas fa-sync-alt mr-2"></i>
                                                Sincronizar Calendar
                                              </>
                                            )}
                                          </button>

                                          {calendarios.length === 0 && (
                                            <button
                                              type="button"
                                              className="btn btn-outline-primary"
                                              onClick={carregarCalendarios}
                                              disabled={carregandoCalendarios}
                                            >
                                              {carregandoCalendarios ? (
                                                <>
                                                  <span className="spinner-border spinner-border-sm mr-2"></span>
                                                  Carregando...
                                                </>
                                              ) : (
                                                <>
                                                  <i className="fas fa-calendar mr-2"></i>
                                                  Carregar Calendários
                                                </>
                                              )}
                                            </button>
                                          )}
                                        </div>
                                      </div>

                                      <div className="col-md-6">
                                        <h6>
                                          <i className="fas fa-plus mr-2"></i>
                                          Criar Evento de Teste
                                        </h6>

                                        <div className="form-group">
                                          <label htmlFor="tituloTeste">Título do Evento</label>
                                          <input
                                            type="text"
                                            className="form-control"
                                            id="tituloTeste"
                                            value={eventoTeste.titulo}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                              setEventoTeste(prev => ({ ...prev, titulo: e.target.value }))
                                            }
                                          />
                                        </div>

                                        <div className="row">
                                          <div className="col-md-6">
                                            <div className="form-group">
                                              <label htmlFor="dataInicioTeste">Data Início</label>
                                              <input
                                                type="date"
                                                className="form-control"
                                                id="dataInicioTeste"
                                                value={eventoTeste.dataInicio}
                                                onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                                  setEventoTeste(prev => ({ ...prev, dataInicio: e.target.value }))
                                                }
                                              />
                                            </div>
                                          </div>
                                          <div className="col-md-6">
                                            <div className="form-group">
                                              <label htmlFor="horaInicioTeste">Hora Início</label>
                                              <input
                                                type="time"
                                                className="form-control"
                                                id="horaInicioTeste"
                                                value={eventoTeste.horaInicio}
                                                onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                                  setEventoTeste(prev => ({ ...prev, horaInicio: e.target.value }))
                                                }
                                              />
                                            </div>
                                          </div>
                                        </div>

                                        <div className="row">
                                          <div className="col-md-6">
                                            <div className="form-group">
                                              <label htmlFor="dataFimTeste">Data Fim</label>
                                              <input
                                                type="date"
                                                className="form-control"
                                                id="dataFimTeste"
                                                value={eventoTeste.dataFim}
                                                onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                                  setEventoTeste(prev => ({ ...prev, dataFim: e.target.value }))
                                                }
                                              />
                                            </div>
                                          </div>
                                          <div className="col-md-6">
                                            <div className="form-group">
                                              <label htmlFor="horaFimTeste">Hora Fim</label>
                                              <input
                                                type="time"
                                                className="form-control"
                                                id="horaFimTeste"
                                                value={eventoTeste.horaFim}
                                                onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                                  setEventoTeste(prev => ({ ...prev, horaFim: e.target.value }))
                                                }
                                              />
                                            </div>
                                          </div>
                                        </div>

                                        <div className="form-group">
                                          <label htmlFor="descricaoTeste">Descrição</label>
                                          <textarea
                                            className="form-control"
                                            id="descricaoTeste"
                                            rows={3}
                                            value={eventoTeste.descricao}
                                            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => 
                                              setEventoTeste(prev => ({ ...prev, descricao: e.target.value }))
                                            }
                                          />
                                        </div>

                                        <button
                                          type="button"
                                          className="btn btn-success"
                                          onClick={criarEventoTeste}
                                          disabled={criandoEventoTeste || !calendarioSelecionado}
                                        >
                                          {criandoEventoTeste ? (
                                            <>
                                              <span className="spinner-border spinner-border-sm mr-2"></span>
                                              Criando...
                                            </>
                                          ) : (
                                            <>
                                              <i className="fas fa-plus mr-2"></i>
                                              Criar Evento de Teste
                                            </>
                                          )}
                                        </button>

                                        {!calendarioSelecionado && (
                                          <small className="form-text text-muted">
                                            Selecione um calendário primeiro
                                          </small>
                                        )}
                                      </div>

                                      <div className="col-md-6">
                                        <h6>
                                          <i className="fas fa-trash mr-2"></i>
                                          Excluir Evento
                                        </h6>

                                        <div className="mb-3">
                                          <div className="form-label">Filtrar eventos por período:</div>
                                          <div className="row">
                                            <div className="col-md-6">
                                              <label htmlFor="dataInicio" className="form-label small">Data Início:</label>
                                              <input
                                                type="date"
                                                className="form-control form-control-sm"
                                                id="dataInicio"
                                                value={filtroEventos.dataInicio}
                                                onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                                  setFiltroEventos(prev => ({ ...prev, dataInicio: e.target.value }))
                                                }
                                              />
                                            </div>
                                            <div className="col-md-6">
                                              <label htmlFor="dataFim" className="form-label small">Data Fim:</label>
                                              <input
                                                type="date"
                                                className="form-control form-control-sm"
                                                id="dataFim"
                                                value={filtroEventos.dataFim}
                                                onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                                  setFiltroEventos(prev => ({ ...prev, dataFim: e.target.value }))
                                                }
                                              />
                                            </div>
                                          </div>
                                          <small className="form-text text-muted">
                                            Deixe em branco para buscar: últimos 30 dias + próximos 60 dias
                                          </small>
                                          <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm mt-1"
                                            onClick={() => setFiltroEventos({ dataInicio: '', dataFim: '' })}
                                          >
                                            <i className="fas fa-eraser mr-1"></i>
                                            Limpar Filtro
                                          </button>
                                        </div>

                                        <div className="mb-3">
                                          <button
                                            type="button"
                                            className="btn btn-info btn-sm"
                                            onClick={carregarEventos}
                                            disabled={carregandoEventos || !calendarioSelecionado}
                                          >
                                            {carregandoEventos ? (
                                              <>
                                                <span className="spinner-border spinner-border-sm mr-2"></span>
                                                Carregando...
                                              </>
                                            ) : (
                                              <>
                                                <i className="fas fa-list mr-2"></i>
                                                Listar Eventos
                                              </>
                                            )}
                                          </button>
                                          {!calendarioSelecionado && (
                                            <small className="form-text text-muted">
                                              Selecione um calendário primeiro
                                            </small>
                                          )}
                                        </div>

                                        {mostrarEventos && eventos.length > 0 && (
                                          <div className="mb-3">
                                            <div className="form-label">Selecione um evento para excluir ou editar:</div>
                                            <div className="list-group" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                              {eventos.map((evento: any) => (
                                                <div
                                                  key={evento.id}
                                                  className={`list-group-item ${
                                                    eventoParaExcluir.eventId === evento.id ? 'active' : ''
                                                  }`}
                                                >
                                                  <div className="d-flex w-100 justify-content-between">
                                                    <h6 className="mb-1">{evento.summary}</h6>
                                                    <small>
                                                      {evento.start?.dateTime ? (
                                                        <>
                                                          {new Date(evento.start.dateTime).toLocaleDateString('pt-BR')} às {' '}
                                                          {new Date(evento.start.dateTime).toLocaleTimeString('pt-BR', { 
                                                            hour: '2-digit', 
                                                            minute: '2-digit' 
                                                          })}
                                                        </>
                                                      ) : evento.start?.date ? (
                                                        new Date(evento.start.date + 'T00:00:00').toLocaleDateString('pt-BR')
                                                      ) : (
                                                        'Data não definida'
                                                      )}
                                                    </small>
                                                  </div>
                                                  <p className="mb-1 text-muted">
                                                    <small>
                                                      <strong>ID:</strong> {evento.id}
                                                      {evento.location && (
                                                        <> • <strong>Local:</strong> {evento.location}</>
                                                      )}
                                                      {evento.status && (
                                                        <> • <strong>Status:</strong> {
                                                          evento.status === 'confirmed' ? 'Confirmado' :
                                                          evento.status === 'tentative' ? 'Tentativo' :
                                                          evento.status === 'cancelled' ? 'Cancelado' : evento.status
                                                        }</>
                                                      )}
                                                    </small>
                                                  </p>
                                                  {evento.description && (
                                                    <small className="text-muted">
                                                      {evento.description.length > 100 
                                                        ? evento.description.substring(0, 100) + '...' 
                                                        : evento.description
                                                      }
                                                    </small>
                                                  )}
                                                  
                                                  <div className="btn-group mt-2" role="group">
                                                    <button
                                                      type="button"
                                                      className="btn btn-sm btn-warning"
                                                      onClick={() => {
                                                        const dataInicio = evento.start?.dateTime 
                                                          ? new Date(evento.start.dateTime) 
                                                          : new Date();
                                                        const dataFim = evento.end?.dateTime 
                                                          ? new Date(evento.end.dateTime) 
                                                          : new Date(dataInicio.getTime() + 60*60*1000);
                                                        
                                                        setEventoParaEditar({
                                                          eventId: evento.id,
                                                          titulo: evento.summary ?? '',
                                                          descricao: evento.description ?? '',
                                                          dataInicio: dataInicio.toISOString().split('T')[0],
                                                          horaInicio: dataInicio.toTimeString().substring(0, 5),
                                                          dataFim: dataFim.toISOString().split('T')[0],
                                                          horaFim: dataFim.toTimeString().substring(0, 5)
                                                        });
                                                      }}
                                                    >
                                                      <i className="fas fa-edit mr-1"></i>
                                                      Editar
                                                    </button>
                                                    <button
                                                      type="button"
                                                      className="btn btn-sm btn-danger"
                                                      onClick={() => 
                                                        setEventoParaExcluir({ 
                                                          eventId: evento.id,
                                                          calendarioId: calendarioSelecionado 
                                                        })
                                                      }
                                                    >
                                                      <i className="fas fa-trash mr-1"></i>
                                                      Excluir
                                                    </button>
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}

                                        {mostrarEventos && eventos.length === 0 && (
                                          <div className="alert alert-info">
                                            <i className="fas fa-info-circle mr-2"></i>
                                            <strong>Nenhum evento encontrado</strong>
                                            <br />
                                            Período pesquisado: últimos 30 dias + próximos 60 dias.
                                            <br />
                                            <small>
                                              Crie um evento de teste ou verifique se há eventos no seu Google Calendar.
                                              <br />
                                              <a 
                                                href="https://calendar.google.com/calendar/render?action=TEMPLATE&text=Evento%20de%20Teste&dates=20250126T140000Z/20250126T150000Z&details=Evento%20criado%20para%20teste%20de%20integração"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-sm btn-outline-primary mt-2"
                                              >
                                                <i className="fas fa-plus mr-1"></i>
                                                Criar evento de teste no Google Calendar
                                              </a>
                                            </small>
                                          </div>
                                        )}

                                        <div className="form-group">
                                          <label htmlFor="eventoIdExcluir">ID do Evento</label>
                                          <input
                                            type="text"
                                            className="form-control"
                                            id="eventoIdExcluir"
                                            placeholder="ou digite o ID do evento manualmente"
                                            value={eventoParaExcluir.eventId}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                              setEventoParaExcluir(prev => ({ ...prev, eventId: e.target.value }))
                                            }
                                          />
                                          <small className="form-text text-muted">
                                            Selecione um evento da lista acima ou digite o ID manualmente
                                          </small>
                                        </div>

                                        <button
                                          type="button"
                                          className="btn btn-danger"
                                          onClick={excluirEvento}
                                          disabled={excluindoEvento || !eventoParaExcluir.eventId.trim() || !calendarioSelecionado}
                                        >
                                          {excluindoEvento ? (
                                            <>
                                              <span className="spinner-border spinner-border-sm mr-2"></span>
                                              Excluindo...
                                            </>
                                          ) : (
                                            <>
                                              <i className="fas fa-trash mr-2"></i>
                                              Excluir Evento
                                            </>
                                          )}
                                        </button>

                                        {(!calendarioSelecionado || !eventoParaExcluir.eventId.trim()) && (
                                          <small className="form-text text-muted">
                                            {!calendarioSelecionado 
                                              ? 'Selecione um calendário primeiro' 
                                              : 'Digite o ID do evento para excluir'
                                            }
                                          </small>
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Seção de Editar Evento */}
                                  <div className="col-md-6">
                                    <div className="card">
                                      <div className="card-header bg-warning text-dark">
                                        <h6 className="mb-0">
                                          <i className="fas fa-edit mr-2"></i>
                                          Editar Evento
                                        </h6>
                                      </div>
                                      <div className="card-body">
                                        <div className="form-group">
                                          <label htmlFor="eventoIdEditar">ID do Evento</label>
                                          <input
                                            type="text"
                                            className="form-control"
                                            id="eventoIdEditar"
                                            placeholder="ID do evento para editar"
                                            value={eventoParaEditar.eventId}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                              setEventoParaEditar(prev => ({ ...prev, eventId: e.target.value }))
                                            }
                                          />
                                          <small className="form-text text-muted">
                                            Selecione um evento da lista acima ou digite o ID manualmente
                                          </small>
                                        </div>

                                        <div className="form-group">
                                          <label htmlFor="tituloEditar">Título</label>
                                          <input
                                            type="text"
                                            className="form-control"
                                            id="tituloEditar"
                                            placeholder="Novo título do evento"
                                            value={eventoParaEditar.titulo}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                              setEventoParaEditar(prev => ({ ...prev, titulo: e.target.value }))
                                            }
                                          />
                                        </div>

                                        <div className="form-group">
                                          <label htmlFor="descricaoEditar">Descrição</label>
                                          <textarea
                                            className="form-control"
                                            id="descricaoEditar"
                                            rows={3}
                                            placeholder="Nova descrição do evento"
                                            value={eventoParaEditar.descricao}
                                            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => 
                                              setEventoParaEditar(prev => ({ ...prev, descricao: e.target.value }))
                                            }
                                          />
                                        </div>

                                        <div className="row">
                                          <div className="col-md-6">
                                            <label htmlFor="dataInicioEditar">Data/Hora Início</label>
                                            <div className="row">
                                              <div className="col-7">
                                                <input
                                                  type="date"
                                                  className="form-control"
                                                  id="dataInicioEditar"
                                                  value={eventoParaEditar.dataInicio}
                                                  onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                                    setEventoParaEditar(prev => ({ ...prev, dataInicio: e.target.value }))
                                                  }
                                                />
                                              </div>
                                              <div className="col-5">
                                                <input
                                                  type="time"
                                                  className="form-control"
                                                  value={eventoParaEditar.horaInicio}
                                                  onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                                    setEventoParaEditar(prev => ({ ...prev, horaInicio: e.target.value }))
                                                  }
                                                />
                                              </div>
                                            </div>
                                          </div>
                                          <div className="col-md-6">
                                            <label htmlFor="dataFimEditar">Data/Hora Fim</label>
                                            <div className="row">
                                              <div className="col-7">
                                                <input
                                                  type="date"
                                                  className="form-control"
                                                  id="dataFimEditar"
                                                  value={eventoParaEditar.dataFim}
                                                  onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                                    setEventoParaEditar(prev => ({ ...prev, dataFim: e.target.value }))
                                                  }
                                                />
                                              </div>
                                              <div className="col-5">
                                                <input
                                                  type="time"
                                                  className="form-control"
                                                  value={eventoParaEditar.horaFim}
                                                  onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                                    setEventoParaEditar(prev => ({ ...prev, horaFim: e.target.value }))
                                                  }
                                                />
                                              </div>
                                            </div>
                                          </div>
                                        </div>

                                        <button
                                          type="button"
                                          className="btn btn-warning mt-3"
                                          onClick={editarEvento}
                                          disabled={editandoEvento || !eventoParaEditar.eventId.trim() || !eventoParaEditar.titulo.trim() || !calendarioSelecionado}
                                        >
                                          {editandoEvento ? (
                                            <>
                                              <span className="spinner-border spinner-border-sm mr-2"></span>
                                              Editando...
                                            </>
                                          ) : (
                                            <>
                                              <i className="fas fa-edit mr-2"></i>
                                              Editar Evento
                                            </>
                                          )}
                                        </button>

                                        {(!calendarioSelecionado || !eventoParaEditar.eventId.trim() || !eventoParaEditar.titulo.trim()) && (
                                          <small className="form-text text-muted">
                                            {!calendarioSelecionado 
                                              ? 'Selecione um calendário primeiro' 
                                              : !eventoParaEditar.eventId.trim()
                                                ? 'Digite o ID do evento para editar'
                                                : 'Digite um título para o evento'
                                            }
                                          </small>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {!integracaoGoogle.agendaHabilitada && (
                                <div className="alert alert-light">
                                  <i className="fas fa-info-circle mr-2"></i>
                                  <strong>Sincronização desabilitada.</strong> 
                                  <br />
                                  Ative a sincronização para criar eventos automaticamente no Google Calendar quando consultas forem agendadas.
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Conta não conectada
                  <div className="row">
                    <div className="col-md-8 mx-auto">
                      <div className="card card-outline card-warning text-center">
                        <div className="card-body p-5">
                          <i className="fab fa-google fa-5x text-muted mb-4"></i>
                          
                          <h4 className="text-muted mb-3">Conectar Conta Google</h4>
                          
                          <p className="text-muted mb-4">
                            Para enviar e-mails através do sistema, conecte sua conta Google Gmail. 
                            <br />
                            O processo é seguro e você pode desconectar a qualquer momento.
                          </p>

                          <div className="alert alert-light">
                            <h6 className="mb-2">
                              <i className="fas fa-shield-alt mr-2 text-success"></i>
                              Sua segurança é nossa prioridade
                            </h6>
                            <ul className="list-unstyled text-left mb-0">
                              <li><i className="fas fa-check text-success mr-2"></i> Conexão criptografada via OAuth2</li>
                              <li><i className="fas fa-check text-success mr-2"></i> Não armazenamos sua senha do Google</li>
                              <li><i className="fas fa-check text-success mr-2"></i> Você pode revogar acesso a qualquer momento</li>
                              <li><i className="fas fa-check text-success mr-2"></i> Permissões limitadas apenas ao necessário</li>
                            </ul>
                          </div>

                          <button
                            type="button"
                            className="btn btn-primary btn-lg"
                            onClick={conectarContaGoogle}
                            disabled={conectandoGoogle}
                          >
                            {conectandoGoogle ? (
                              <>
                                <span className="spinner-border spinner-border-sm mr-2"></span>
                                Conectando...
                              </>
                            ) : (
                              <>
                                <i className="fab fa-google mr-2"></i>
                                Conectar com Google
                              </>
                            )}
                          </button>

                          <p className="text-muted mt-3 small">
                            Ao conectar, você será redirecionado para o Google para autorizar o acesso.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Aba: Personalizar Notificações */}
            {abaSelecionada === 'notificacoes' && (
              <div className="tab-pane active">
                <div className="alert alert-info">
                  <h5><i className="fas fa-palette mr-1"></i>{' '}Personalização de Notificações</h5>
                  <p className="mb-0">
                    Personalize a aparência dos emails e eventos do Google Calendar de forma simples e visual.
                    Seu logo e cores serão aplicados automaticamente!
                  </p>
                </div>

                {/* Seção: Identidade Visual */}
                <div className="card mb-4">
                  <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">
                      <i className="fas fa-paint-brush mr-2"></i>
                      Identidade Visual
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      {/* Logo */}
                      <div className="col-md-6 mb-4">
                        <div className="card h-100 border-primary">
                          <div className="card-header bg-light">
                            <h6 className="mb-0">
                              <i className="fas fa-image mr-2 text-primary"></i>
                              Logo nos Emails
                            </h6>
                          </div>
                          <div className="card-body text-center">
                            {logoUrl ? (
                              <div className="mb-3">
                                <img 
                                  src={logoUrl} 
                                  alt="Logo da empresa" 
                                  style={{ maxWidth: '150px', maxHeight: '80px', objectFit: 'contain' }}
                                  className="border rounded p-2"
                                />
                              </div>
                            ) : (
                              <div className="mb-3 p-4 bg-light rounded">
                                <i className="fas fa-building fa-3x text-muted"></i>
                                <p className="text-muted mt-2 mb-0 small">Nenhum logo cadastrado</p>
                              </div>
                            )}
                            <div className="custom-control custom-switch d-inline-block">
                              <input
                                type="checkbox"
                                className="custom-control-input"
                                id="usarLogoEmpresa"
                                checked={templatesNotificacao.usarLogoEmpresa ?? false}
                                onChange={(e) => setTemplatesNotificacao(prev => ({
                                  ...prev,
                                  usarLogoEmpresa: e.target.checked
                                }))}
                              />
                              <label className="custom-control-label" htmlFor="usarLogoEmpresa">
                                Incluir logo nos emails
                              </label>
                            </div>
                            <p className="small text-muted mt-2 mb-0">
                              <i className="fas fa-info-circle mr-1"></i>
                              Cadastre seu logo na aba "Empresa"
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Cores */}
                      <div className="col-md-6 mb-4">
                        <div className="card h-100 border-success">
                          <div className="card-header bg-light">
                            <h6 className="mb-0">
                              <i className="fas fa-palette mr-2 text-success"></i>
                              Cores do Template
                            </h6>
                          </div>
                          <div className="card-body">
                            <div className="row">
                              <div className="col-6 mb-3">
                                <label className="small text-muted d-block mb-1" htmlFor="templatesCorPrimaria">Cor Principal</label>
                                <div className="d-flex align-items-center">
                                  <input
                                    id="templatesCorPrimaria"
                                    type="color"
                                    className="form-control form-control-color"
                                    value={templatesNotificacao.corPrimaria || '#4a90a4'}
                                    onChange={(e) => setTemplatesNotificacao(prev => ({
                                      ...prev,
                                      corPrimaria: e.target.value
                                    }))}
                                    style={{ width: '50px', height: '38px', padding: '2px' }}
                                  />
                                  <span className="ml-2 small">{templatesNotificacao.corPrimaria || '#4a90a4'}</span>
                                </div>
                              </div>
                              <div className="col-6 mb-3">
                                <label className="small text-muted d-block mb-1" htmlFor="templatesCorSecundaria">Cor de Destaque</label>
                                <div className="d-flex align-items-center">
                                  <input
                                    id="templatesCorSecundaria"
                                    type="color"
                                    className="form-control form-control-color"
                                    value={templatesNotificacao.corSecundaria || '#28a745'}
                                    onChange={(e) => setTemplatesNotificacao(prev => ({
                                      ...prev,
                                      corSecundaria: e.target.value
                                    }))}
                                    style={{ width: '50px', height: '38px', padding: '2px' }}
                                  />
                                  <span className="ml-2 small">{templatesNotificacao.corSecundaria || '#28a745'}</span>
                                </div>
                              </div>
                              <div className="col-6 mb-3">
                                <label className="small text-muted d-block mb-1" htmlFor="templatesCorTexto">Cor do Texto</label>
                                <div className="d-flex align-items-center">
                                  <input
                                    id="templatesCorTexto"
                                    type="color"
                                    className="form-control form-control-color"
                                    value={templatesNotificacao.corTexto || '#333333'}
                                    onChange={(e) => setTemplatesNotificacao(prev => ({
                                      ...prev,
                                      corTexto: e.target.value
                                    }))}
                                    style={{ width: '50px', height: '38px', padding: '2px' }}
                                  />
                                  <span className="ml-2 small">{templatesNotificacao.corTexto || '#333333'}</span>
                                </div>
                              </div>
                              <div className="col-6 mb-3">
                                <label className="small text-muted d-block mb-1" htmlFor="templatesCorFundo">Cor de Fundo</label>
                                <div className="d-flex align-items-center">
                                  <input
                                    id="templatesCorFundo"
                                    type="color"
                                    className="form-control form-control-color"
                                    value={templatesNotificacao.corFundo || '#f8f9fa'}
                                    onChange={(e) => setTemplatesNotificacao(prev => ({
                                      ...prev,
                                      corFundo: e.target.value
                                    }))}
                                    style={{ width: '50px', height: '38px', padding: '2px' }}
                                  />
                                  <span className="ml-2 small">{templatesNotificacao.corFundo || '#f8f9fa'}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Seção: Textos Personalizados */}
                <div className="card mb-4">
                  <div className="card-header bg-info text-white">
                    <h5 className="mb-0">
                      <i className="fas fa-edit mr-2"></i>
                      Textos das Notificações
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="nomeExibicao">
                            <i className="fas fa-building mr-2"></i>
                            Nome do Consultório/Clínica
                          </label>
                          <input
                            type="text"
                            id="nomeExibicao"
                            className="form-control"
                            value={templatesNotificacao.nomeExibicao ?? ''}
                            onChange={(e) => setTemplatesNotificacao(prev => ({
                              ...prev,
                              nomeExibicao: e.target.value
                            }))}
                            placeholder="Ex: Consultório de Psicologia Maria Silva"
                          />
                          <small className="form-text text-muted">
                            Aparecerá no cabeçalho do email
                          </small>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="telefoneContato">
                            <i className="fas fa-phone mr-2"></i>
                            Telefone para Contato
                          </label>
                          <input
                            type="text"
                            id="telefoneContato"
                            className="form-control"
                            value={templatesNotificacao.telefoneContato ?? ''}
                            onChange={(e) => setTemplatesNotificacao(prev => ({
                              ...prev,
                              telefoneContato: e.target.value
                            }))}
                            placeholder="Ex: (11) 99999-9999"
                          />
                          <small className="form-text text-muted">
                            Exibido no rodapé do email
                          </small>
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="mensagemBoasVindas">
                        <i className="fas fa-comment-dots mr-2"></i>
                        Mensagem de Confirmação
                      </label>
                      <textarea
                        id="mensagemBoasVindas"
                        className="form-control"
                        rows={2}
                        value={templatesNotificacao.mensagemBoasVindas ?? ''}
                        onChange={(e) => setTemplatesNotificacao(prev => ({
                          ...prev,
                          mensagemBoasVindas: e.target.value
                        }))}
                        placeholder="Ex: Olá! Sua consulta foi confirmada com sucesso."
                      />
                      <small className="form-text text-muted">
                        Mensagem inicial do email de confirmação. Use <code>{'{paciente_nome}'}</code> para incluir o nome do paciente.
                      </small>
                    </div>

                    <div className="form-group">
                      <label htmlFor="enderecoConsultorio">
                        <i className="fas fa-map-marker-alt mr-2"></i>
                        Endereço do Consultório
                      </label>
                      <input
                        type="text"
                        id="enderecoConsultorio"
                        className="form-control"
                        value={templatesNotificacao.enderecoConsultorio ?? ''}
                        onChange={(e) => setTemplatesNotificacao(prev => ({
                          ...prev,
                          enderecoConsultorio: e.target.value
                        }))}
                        placeholder="Ex: Rua das Flores, 123 - Sala 45 - Centro - São Paulo/SP"
                      />
                      <small className="form-text text-muted">
                        Endereço completo para consultas presenciais
                      </small>
                    </div>

                    <div className="form-group">
                      <label htmlFor="mensagemRodape">
                        <i className="fas fa-signature mr-2"></i>
                        Mensagem do Rodapé
                      </label>
                      <textarea
                        id="mensagemRodape"
                        className="form-control"
                        rows={2}
                        value={templatesNotificacao.mensagemRodape ?? ''}
                        onChange={(e) => setTemplatesNotificacao(prev => ({
                          ...prev,
                          mensagemRodape: e.target.value
                        }))}
                        placeholder="Ex: Qualquer dúvida, entre em contato. Aguardo você!"
                      />
                      <small className="form-text text-muted">
                        Mensagem final que aparecerá no rodapé do email
                      </small>
                    </div>
                  </div>
                </div>

                {/* Seção: Configurações de Conteúdo */}
                <div className="card mb-4">
                  <div className="card-header bg-secondary text-white">
                    <h5 className="mb-0">
                      <i className="fas fa-cogs mr-2"></i>
                      O que incluir nas notificações
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="custom-control custom-switch mb-3">
                          <input
                            type="checkbox"
                            className="custom-control-input"
                            id="incluirLinkConsulta"
                            checked={templatesNotificacao.incluirLinkConsulta ?? false}
                            onChange={(e) => setTemplatesNotificacao(prev => ({
                              ...prev,
                              incluirLinkConsulta: e.target.checked
                            }))}
                          />
                          <label className="custom-control-label" htmlFor="incluirLinkConsulta">
                            <strong>Link da consulta online</strong>
                            <small className="d-block text-muted">Incluir botão/link para acessar a consulta</small>
                          </label>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="custom-control custom-switch mb-3">
                          <input
                            type="checkbox"
                            className="custom-control-input"
                            id="incluirObservacoesImportantes"
                            checked={templatesNotificacao.incluirObservacoesImportantes ?? false}
                            onChange={(e) => setTemplatesNotificacao(prev => ({
                              ...prev,
                              incluirObservacoesImportantes: e.target.checked
                            }))}
                          />
                          <label className="custom-control-label" htmlFor="incluirObservacoesImportantes">
                            <strong>Observações importantes</strong>
                            <small className="d-block text-muted">Incluir dicas e lembretes padrão</small>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Seção: Google Calendar */}
                <div className="card mb-4">
                  <div className="card-header" style={{ backgroundColor: '#4285f4', color: 'white' }}>
                    <h5 className="mb-0">
                      <i className="fab fa-google mr-2"></i>
                      Eventos do Google Calendar
                    </h5>
                  </div>
                  <div className="card-body">
                    <p className="text-muted mb-4">
                      Configure como os eventos aparecerão na agenda do Google Calendar
                    </p>
                    
                    <div className="form-group">
                      <label htmlFor="calendarTitulo">
                        <i className="fas fa-heading mr-2"></i>
                        Título do Evento
                      </label>
                      <input
                        type="text"
                        id="calendarTitulo"
                        className="form-control"
                        value={templatesNotificacao.calendarTituloTemplate ?? ''}
                        onChange={(e) => setTemplatesNotificacao(prev => ({
                          ...prev,
                          calendarTituloTemplate: e.target.value
                        }))}
                        placeholder="Ex: Consulta - {paciente_nome}"
                      />
                      <small className="form-text text-muted">
                        Use <code>{'{paciente_nome}'}</code> para incluir o nome do paciente automaticamente
                      </small>
                    </div>

                    <div className="form-group">
                      <label htmlFor="calendarDescricao">
                        <i className="fas fa-align-left mr-2"></i>
                        Descrição do Evento
                      </label>
                      <textarea
                        id="calendarDescricao"
                        className="form-control"
                        rows={5}
                        value={templatesNotificacao.calendarDescricaoTemplate ?? ''}
                        onChange={(e) => setTemplatesNotificacao(prev => ({
                          ...prev,
                          calendarDescricaoTemplate: e.target.value
                        }))}
                        placeholder="📅 Consulta agendada&#10;👤 Paciente: {paciente_nome}&#10;💰 Valor: R$ {valor_consulta}&#10;&#10;🔗 Acesse pelo link: {link_consulta_online}"
                      />
                      <small className="form-text text-muted">
                        Variáveis disponíveis: <code>{'{paciente_nome}'}</code>, <code>{'{data_consulta}'}</code>, <code>{'{hora_consulta}'}</code>, <code>{'{valor_consulta}'}</code>, <code>{'{psicologo_nome}'}</code>, <code>{'{link_consulta_online}'}</code>
                      </small>
                    </div>

                    <div className="form-group">
                      <label htmlFor="calendarLocalizacao">
                        <i className="fas fa-map-marker-alt mr-2"></i>
                        Localização
                      </label>
                      <input
                        type="text"
                        id="calendarLocalizacao"
                        className="form-control"
                        value={templatesNotificacao.calendarLocalizacaoTemplate ?? ''}
                        onChange={(e) => setTemplatesNotificacao(prev => ({
                          ...prev,
                          calendarLocalizacaoTemplate: e.target.value
                        }))}
                        placeholder="Ex: {link_consulta_online} ou endereço físico"
                      />
                      <small className="form-text text-muted">
                        Use <code>{'{link_consulta_online}'}</code> para consultas online ou digite o endereço para consultas presenciais
                      </small>
                    </div>

                    {/* Preview do Google Calendar */}
                    <div className="mt-4 pt-3 border-top">
                      <h6 className="mb-3">
                        <i className="fas fa-eye mr-2"></i>
                        Pré-visualização do Evento
                      </h6>
                      <div 
                        style={{ 
                          backgroundColor: 'white',
                          border: '1px solid #dadce0',
                          borderRadius: '8px',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                          overflow: 'hidden'
                        }}
                      >
                        {/* Header do evento estilo Google Calendar */}
                        <div 
                          style={{ 
                            backgroundColor: '#4285f4',
                            padding: '12px 16px',
                            color: 'white'
                          }}
                        >
                          <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>
                            <i className="far fa-calendar mr-1"></i>
                            Segunda-feira, 02 de Dezembro • 14:00 - 15:00
                          </div>
                          <div style={{ fontSize: '18px', fontWeight: 500 }}>
                            {(templatesNotificacao.calendarTituloTemplate || 'Consulta - {paciente_nome}')
                              .replace('{paciente_nome}', 'João Silva')
                              .replace('{data_consulta}', '02/12/2025')
                              .replace('{hora_consulta}', '14:00')
                            }
                          </div>
                        </div>
                        
                        {/* Corpo do evento */}
                        <div style={{ padding: '16px' }}>
                          {/* Localização */}
                          {templatesNotificacao.calendarLocalizacaoTemplate && (
                            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
                              <i className="fas fa-map-marker-alt" style={{ color: '#5f6368', marginRight: '12px', marginTop: '2px' }}></i>
                              <span style={{ color: '#1a73e8', fontSize: '14px' }}>
                                {templatesNotificacao.calendarLocalizacaoTemplate
                                  .replace('{link_consulta_online}', 'https://meet.google.com/abc-defg-hij')
                                  .replace('{paciente_nome}', 'João Silva')
                                }
                              </span>
                            </div>
                          )}
                          
                          {/* Descrição */}
                          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                            <i className="fas fa-align-left" style={{ color: '#5f6368', marginRight: '12px', marginTop: '2px' }}></i>
                            <div style={{ fontSize: '14px', color: '#3c4043', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                              {(templatesNotificacao.calendarDescricaoTemplate || '📅 Consulta agendada\n👤 Paciente: {paciente_nome}\n💰 Valor: R$ {valor_consulta}')
                                .replace(/{paciente_nome}/g, 'João Silva')
                                .replace(/{data_consulta}/g, '02/12/2025')
                                .replace(/{hora_consulta}/g, '14:00')
                                .replace(/{valor_consulta}/g, '200,00')
                                .replace(/{psicologo_nome}/g, 'Dra. Maria Santos')
                                .replace(/{link_consulta_online}/g, 'https://meet.google.com/abc-defg-hij')
                              }
                            </div>
                          </div>
                          
                          {/* Participante (paciente) */}
                          <div style={{ display: 'flex', alignItems: 'center', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #e0e0e0' }}>
                            <i className="fas fa-user" style={{ color: '#5f6368', marginRight: '12px' }}></i>
                            <div>
                              <div style={{ fontSize: '14px', color: '#3c4043' }}>João Silva</div>
                              <div style={{ fontSize: '12px', color: '#5f6368' }}>joao.silva@email.com • Convidado</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Preview Visual */}
                <div className="card mb-4">
                  <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      <i className="fas fa-eye mr-2"></i>
                      Pré-visualização do Email
                    </h5>
                    <button 
                      type="button" 
                      className="btn btn-outline-light btn-sm"
                      onClick={() => setMostrarPreviewTemplates(true)}
                    >
                      <i className="fas fa-expand mr-1"></i>
                      Ver em Tela Cheia
                    </button>
                  </div>
                  <div className="card-body p-0">
                    <div 
                      style={{ 
                        backgroundColor: templatesNotificacao.corFundo || '#f8f9fa',
                        padding: '20px',
                        minHeight: '300px'
                      }}
                    >
                      {/* Cabeçalho */}
                      <div 
                        style={{ 
                          backgroundColor: templatesNotificacao.corPrimaria || '#4a90a4',
                          color: 'white',
                          padding: '20px',
                          borderRadius: '8px 8px 0 0',
                          textAlign: 'center'
                        }}
                      >
                        {templatesNotificacao.usarLogoEmpresa && logoUrl && (
                          <img 
                            src={logoUrl} 
                            alt="Logo" 
                            style={{ maxHeight: '50px', marginBottom: '10px' }}
                          />
                        )}
                        <h4 style={{ margin: 0 }}>
                          {templatesNotificacao.nomeExibicao || 'Seu Consultório'}
                        </h4>
                      </div>
                      
                      {/* Corpo */}
                      <div 
                        style={{ 
                          backgroundColor: 'white',
                          padding: '25px',
                          color: templatesNotificacao.corTexto || '#333333'
                        }}
                      >
                        <p style={{ fontSize: '16px' }}>
                          {templatesNotificacao.mensagemBoasVindas?.replace('{paciente_nome}', 'João Silva') || 'Olá João Silva, sua consulta foi confirmada!'}
                        </p>
                        
                        <div 
                          style={{ 
                            backgroundColor: '#f5f5f5',
                            padding: '15px',
                            borderRadius: '8px',
                            marginTop: '15px',
                            borderLeft: `4px solid ${templatesNotificacao.corSecundaria || '#28a745'}`
                          }}
                        >
                          <p style={{ margin: '5px 0' }}><strong>📅 Data:</strong> 02/12/2025</p>
                          <p style={{ margin: '5px 0' }}><strong>🕐 Horário:</strong> 14:00 - 15:00</p>
                          <p style={{ margin: '5px 0' }}><strong>👨‍⚕️ Profissional:</strong> Dra. Maria Santos</p>
                          <p style={{ margin: '5px 0' }}><strong>💰 Valor:</strong> R$ 200,00</p>
                        </div>

                        {templatesNotificacao.incluirLinkConsulta && (
                          <div style={{ textAlign: 'center', marginTop: '20px' }}>
                            <button 
                              style={{ 
                                backgroundColor: templatesNotificacao.corSecundaria || '#28a745',
                                color: 'white',
                                border: 'none',
                                padding: '12px 30px',
                                borderRadius: '25px',
                                fontSize: '16px',
                                cursor: 'pointer'
                              }}
                            >
                              🔗 Acessar Consulta Online
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {/* Rodapé */}
                      <div 
                        style={{ 
                          backgroundColor: '#f0f0f0',
                          padding: '15px',
                          borderRadius: '0 0 8px 8px',
                          textAlign: 'center',
                          fontSize: '14px',
                          color: '#666'
                        }}
                      >
                        <p style={{ margin: '5px 0' }}>
                          {templatesNotificacao.mensagemRodape || 'Qualquer dúvida, entre em contato.'}
                        </p>
                        {templatesNotificacao.telefoneContato && (
                          <p style={{ margin: '5px 0' }}>
                            <i className="fas fa-phone mr-1"></i> {templatesNotificacao.telefoneContato}
                          </p>
                        )}
                        {templatesNotificacao.enderecoConsultorio && (
                          <p style={{ margin: '5px 0', fontSize: '12px' }}>
                            <i className="fas fa-map-marker-alt mr-1"></i> {templatesNotificacao.enderecoConsultorio}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Botões de Ação */}
                <div className="d-flex justify-content-between flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={async () => {
                      if (confirm('Tem certeza que deseja restaurar as configurações padrão? Suas personalizações serão perdidas.')) {
                        setTemplatesNotificacao(prev => ({
                          ...prev,
                          corPrimaria: '#4a90a4',
                          corSecundaria: '#28a745',
                          corTexto: '#333333',
                          corFundo: '#f8f9fa',
                          usarLogoEmpresa: true,
                          nomeExibicao: '',
                          mensagemBoasVindas: 'Olá {paciente_nome}, sua consulta foi confirmada!',
                          mensagemRodape: 'Qualquer dúvida, entre em contato.',
                          estiloTemplate: 'moderno'
                        }));
                        setSucesso('Configurações padrão restauradas!');
                        setTimeout(() => setSucesso(null), 3000);
                      }
                    }}
                    disabled={salvandoTemplates}
                  >
                    <i className="fas fa-undo mr-2"></i>
                    Restaurar Padrão
                  </button>
                  
                  <button
                    type="button"
                    className="btn btn-success btn-lg"
                    onClick={salvarConfiguracoes}
                    disabled={salvandoTemplates}
                  >
                    {salvandoTemplates ? (
                      <>
                        <span className="spinner-border spinner-border-sm mr-2"></span>
                        Salvando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save mr-2"></i>
                        Salvar Personalizações
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Aba: Alertas do Sistema */}
            {abaSelecionada === 'alertas' && (
              <div className="tab-pane active">
                <div className="alert alert-info">
                  <h5><i className="fas fa-bell mr-1"></i>{' '}Alertas e Notificações do Sistema</h5>
                  <p className="mb-0">
                    Configure suas preferências de notificações in-app, incluindo tipos de alertas e tempos de antecedência.
                  </p>
                </div>

                {carregandoPreferenciasAlertas ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary mb-3"></div>
                    <p>Carregando preferências...</p>
                  </div>
                ) : (
                  <>
                    {/* Configurações Gerais */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h5 className="mb-0">
                          <i className="fas fa-cog mr-2"></i>
                          Configurações Gerais
                        </h5>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group">
                              <div className="custom-control custom-switch">
                                <input
                                  type="checkbox"
                                  className="custom-control-input"
                                  id="notificacoesHabilitadas"
                                  checked={preferenciasAlertas.notificacoes_habilitadas}
                                  onChange={(e) =>
                                    setPreferenciasAlertas({ ...preferenciasAlertas, notificacoes_habilitadas: e.target.checked })
                                  }
                                />
                                <label className="custom-control-label" htmlFor="notificacoesHabilitadas">
                                  <strong>Habilitar notificações</strong>
                                  <small className="d-block text-muted">Ativar/desativar todas as notificações do sistema</small>
                                </label>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group">
                              <div className="custom-control custom-switch">
                                <input
                                  type="checkbox"
                                  className="custom-control-input"
                                  id="somHabilitado"
                                  checked={preferenciasAlertas.som_habilitado}
                                  onChange={(e) =>
                                    setPreferenciasAlertas({ ...preferenciasAlertas, som_habilitado: e.target.checked })
                                  }
                                />
                                <label className="custom-control-label" htmlFor="somHabilitado">
                                  <strong>Som de notificação</strong>
                                  <small className="d-block text-muted">Reproduzir som ao receber notificações</small>
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Tempos de Alerta */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h5 className="mb-0">
                          <i className="fas fa-clock mr-2"></i>
                          Tempos de Alerta
                        </h5>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label htmlFor="alertaAntesConsulta">
                              <strong>Lembrete antes da consulta</strong>
                            </label>
                            <div className="input-group">
                              <input
                                type="number"
                                className="form-control"
                                id="alertaAntesConsulta"
                                min="5"
                                max="1440"
                                value={preferenciasAlertas.alerta_antes_consulta}
                                onChange={(e) =>
                                  setPreferenciasAlertas({ ...preferenciasAlertas, alerta_antes_consulta: parseInt(e.target.value) })
                                }
                              />
                              <div className="input-group-append">
                                <span className="input-group-text">minutos</span>
                              </div>
                            </div>
                            <small className="text-muted">Receber alerta X minutos antes da consulta (padrão: 15)</small>
                          </div>

                          <div className="col-md-6 mb-3">
                            <label htmlFor="alertaPagamento">
                              <strong>Alerta de pagamento pendente</strong>
                            </label>
                            <div className="input-group">
                              <input
                                type="number"
                                className="form-control"
                                id="alertaPagamento"
                                min="30"
                                max="1440"
                                value={preferenciasAlertas.alerta_pagamento_apos_consulta}
                                onChange={(e) =>
                                  setPreferenciasAlertas({ ...preferenciasAlertas, alerta_pagamento_apos_consulta: parseInt(e.target.value) })
                                }
                              />
                              <div className="input-group-append">
                                <span className="input-group-text">minutos</span>
                              </div>
                            </div>
                            <small className="text-muted">Alertar sobre pagamento não registrado após X minutos da consulta (padrão: 60)</small>
                          </div>

                          <div className="col-md-6 mb-3">
                            <label htmlFor="alertaProntuario">
                              <strong>Alerta de prontuário pendente</strong>
                            </label>
                            <div className="input-group">
                              <input
                                type="number"
                                className="form-control"
                                id="alertaProntuario"
                                min="60"
                                max="2880"
                                value={preferenciasAlertas.alerta_prontuario_pendente}
                                onChange={(e) =>
                                  setPreferenciasAlertas({ ...preferenciasAlertas, alerta_prontuario_pendente: parseInt(e.target.value) })
                                }
                              />
                              <div className="input-group-append">
                                <span className="input-group-text">minutos</span>
                              </div>
                            </div>
                            <small className="text-muted">Alertar sobre prontuário não preenchido após X minutos da consulta (padrão: 120)</small>
                          </div>

                          <div className="col-md-6 mb-3">
                            <label htmlFor="alertaPlano">
                              <strong>Alerta de vencimento de plano</strong>
                            </label>
                            <div className="input-group">
                              <input
                                type="number"
                                className="form-control"
                                id="alertaPlano"
                                min="1"
                                max="30"
                                value={preferenciasAlertas.alerta_plano_vencimento}
                                onChange={(e) =>
                                  setPreferenciasAlertas({ ...preferenciasAlertas, alerta_plano_vencimento: parseInt(e.target.value) })
                                }
                              />
                              <div className="input-group-append">
                                <span className="input-group-text">dias</span>
                              </div>
                            </div>
                            <small className="text-muted">Alertar sobre planos que vencem em X dias (padrão: 7)</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Tipos de Notificações */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h5 className="mb-0">
                          <i className="fas fa-list-check mr-2"></i>
                          Tipos de Notificações
                        </h5>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group">
                              <div className="custom-control custom-switch">
                                <input
                                  type="checkbox"
                                  className="custom-control-input"
                                  id="tipoLembreteConsulta"
                                  checked={preferenciasAlertas.tipo_lembrete_consulta}
                                  onChange={(e) =>
                                    setPreferenciasAlertas({ ...preferenciasAlertas, tipo_lembrete_consulta: e.target.checked })
                                  }
                                />
                                <label className="custom-control-label" htmlFor="tipoLembreteConsulta">
                                  <i className="fas fa-clock text-primary mr-2"></i>
                                  <strong>Lembretes de consulta</strong>
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <div className="custom-control custom-switch">
                                <input
                                  type="checkbox"
                                  className="custom-control-input"
                                  id="tipoPagamentoAprovado"
                                  checked={preferenciasAlertas.tipo_pagamento_aprovado}
                                  onChange={(e) =>
                                    setPreferenciasAlertas({ ...preferenciasAlertas, tipo_pagamento_aprovado: e.target.checked })
                                  }
                                />
                                <label className="custom-control-label" htmlFor="tipoPagamentoAprovado">
                                  <i className="fas fa-check-circle text-success mr-2"></i>
                                  <strong>Pagamento aprovado</strong>
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <div className="custom-control custom-switch">
                                <input
                                  type="checkbox"
                                  className="custom-control-input"
                                  id="tipoPagamentoPendente"
                                  checked={preferenciasAlertas.tipo_pagamento_pendente}
                                  onChange={(e) =>
                                    setPreferenciasAlertas({ ...preferenciasAlertas, tipo_pagamento_pendente: e.target.checked })
                                  }
                                />
                                <label className="custom-control-label" htmlFor="tipoPagamentoPendente">
                                  <i className="fas fa-exclamation-circle text-warning mr-2"></i>
                                  <strong>Pagamento pendente</strong>
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <div className="custom-control custom-switch">
                                <input
                                  type="checkbox"
                                  className="custom-control-input"
                                  id="tipoProntuarioPendente"
                                  checked={preferenciasAlertas.tipo_prontuario_pendente}
                                  onChange={(e) =>
                                    setPreferenciasAlertas({ ...preferenciasAlertas, tipo_prontuario_pendente: e.target.checked })
                                  }
                                />
                                <label className="custom-control-label" htmlFor="tipoProntuarioPendente">
                                  <i className="fas fa-file-medical text-info mr-2"></i>
                                  <strong>Prontuário pendente</strong>
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <div className="custom-control custom-switch">
                                <input
                                  type="checkbox"
                                  className="custom-control-input"
                                  id="tipoPlanoVencendo"
                                  checked={preferenciasAlertas.tipo_plano_vencendo}
                                  onChange={(e) =>
                                    setPreferenciasAlertas({ ...preferenciasAlertas, tipo_plano_vencendo: e.target.checked })
                                  }
                                />
                                <label className="custom-control-label" htmlFor="tipoPlanoVencendo">
                                  <i className="fas fa-exclamation-triangle text-danger mr-2"></i>
                                  <strong>Plano vencendo</strong>
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Botões de Ação */}
                    <div className="text-right">
                      <button
                        type="button"
                        className="btn btn-success btn-lg"
                        onClick={salvarPreferenciasAlertas}
                        disabled={salvandoPreferenciasAlertas}
                      >
                        {salvandoPreferenciasAlertas ? (
                          <>
                            <span className="spinner-border spinner-border-sm mr-2"></span>
                            Salvando...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save mr-2"></i>
                            Salvar Preferências
                          </>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Preview dos Templates */}
      <TemplatePreviewModal
        isOpen={mostrarPreviewTemplates}
        onClose={() => setMostrarPreviewTemplates(false)}
        templates={{
          emailConfirmacaoHtml: templatesNotificacao.emailConfirmacaoHtml ?? '',
          emailCancelamentoHtml: templatesNotificacao.emailCancelamentoHtml ?? '',
          calendarTituloTemplate: templatesNotificacao.calendarTituloTemplate || 'Consulta - {{paciente_nome}}',
          calendarDescricaoTemplate: templatesNotificacao.calendarDescricaoTemplate ?? '',
          calendarLocalizacaoTemplate: templatesNotificacao.calendarLocalizacaoTemplate || '{{link_consulta_online}}'
        }}
      />
    </div>
  );
}
